import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type JSX,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useMutation } from '@tanstack/react-query';
import {
  HELD_ITEMS,
  heldItemById,
  PRESENCE_FLAVORS,
  PRESENCE_LABELS,
  REACTIONS,
  RPS_CHOICES,
  RPS_CHOICE_LABELS,
  SAY_MAX_LEN,
  SOUND_REACTIONS,
  WORLD_EVENT_LABELS,
  ENTRY_DOORWAY_ID,
  PERSONAL_VOICE_MIN,
  PERSONAL_VOICE_MAX,
  parseChatSegments,
  roleAtLeast,
  SPECIES_META,
  type DuckSpecies,
  type Doorway,
  type RoomGroup,
  type Animation,
  type AuthenticatedUser,
  type EquippedCosmetics,
  type GuildInfluenceDto,
  type InteractiveObject,
  type PresenceFlavor,
  type FurnitureItem,
  type ProgressionStatsDto,
  type Reaction,
  type RpsChoice,
  type RpsOutcome,
  type BarkOutcome,
  type Seat,
  type SoundReactionId,
  type Weather,
  type WorldEventKind,
  type WorldThemeId,
} from '@duck/contracts';
import type { CameraStatus, CurrentZone, MicDevice } from '../../net/voice-client';
import type { ConnectionStatus } from '../../net/realtime-client';
import type { EntryActionView, DoorActionView } from './render/office-engine';
import { api } from '../../lib/api';
import { usePeerVolumeStore } from '../../state/peer-volume.store';
import { getBarkScore, subscribeBarkScore } from '../../state/bark-score.store';
import { useViewport } from '../../lib/useViewport';
import { getMicLevel, subscribeMicLevel } from '../../state/mic-level.store';
import { bumpRender } from '../../state/render-tally';
import { nameColorCss } from './render/species';
import { GuildPanels, Panel, type GuildPanelTab } from './GuildPanel';
import { Wardrobe } from './Wardrobe';
import { AudioSettingsPanel } from './AudioSettings';
import { RenderQualityControl } from './RenderSettings';
import { useInteractionMenus, type InteractionMenus, type MenuId } from './useInteractionMenus';
import { prefetchSoundReaction } from './sound/soundboard-audio';

const EMOTES: { key: Animation; icon: string; title: string }[] = [
  { key: 'wave', icon: '👋', title: 'Wave' },
  { key: 'dance', icon: '🕺', title: 'Dance' },
  { key: 'laugh', icon: '😂', title: 'Laugh' },
  { key: 'coffee', icon: '☕', title: 'Coffee' },
  { key: 'spin', icon: '🌀', title: 'Spin' },
  { key: 'jump', icon: '🦘', title: 'Jump' },
  { key: 'panic', icon: '😱', title: 'Panic' },
  { key: 'headbang', icon: '🤘', title: 'Headbang' },
  { key: 'wobble', icon: '🥴', title: 'Wobble' },
  { key: 'vibe', icon: '🪩', title: 'Vibe' },
];

/**
 * The four action-bar interaction menus, in left→right order. Each owns a number HOTKEY (1–4) that opens
 * it, and a number-SELECTOR (registered in {@link ActionBar}) so you can pick an item by typing its
 * number. Ids are strings (not a closed union) so a fifth menu joins by adding an entry here + a hotkey +
 * a selector — no controller changes. See {@link useInteractionMenus}.
 */
const MENU = {
  emoji: 'emoji',
  sound: 'sound',
  animation: 'animation',
  item: 'item',
  doors: 'doors',
  challenge: 'challenge',
} as const satisfies Record<string, MenuId>;

/** Bare number keys that OPEN a menu when none is open (order mirrors the action bar). */
const MENU_HOTKEYS: Readonly<Record<string, MenuId>> = {
  '1': MENU.emoji,
  '2': MENU.sound,
  '3': MENU.animation,
  '4': MENU.item,
};

/**
 * A tiny "press N" chip on a menu item — teaches the type-a-number shortcut (UX Director: the menu IS the
 * cheat-sheet, so the hotkey is discoverable, never hidden). Purely decorative; the button's accessible
 * name already says what it does, and the number key works whether or not anyone reads the chip.
 */
function NumBadge({ n }: { n: number }): JSX.Element {
  return (
    <span
      aria-hidden="true"
      className="shrink-0 rounded-md bg-black/40 px-1.5 py-0.5 text-[10px] font-bold leading-none tabular-nums text-white/55"
    >
      {n}
    </span>
  );
}

/**
 * Shared focus choreography for a controlled action-bar menu (WCAG 2.4.3), so every menu behaves the same
 * whether it's opened by mouse or by a number hotkey:
 *   • on OPEN — pull focus into the popover (`focusOnOpen`) so Esc works at once + a SR user lands in it;
 *   • on CLOSE — restore focus to the toggle, BUT only when no other menu opened (a hotkey SWITCH closes
 *     this one as it opens the next; the next menu claims focus, so we must not yank it back here).
 * Re-renders while open (e.g. the digit buffer ticking) never re-steal focus — the effect only fires on
 * an actual open/close edge.
 */
function useMenuFocus(
  isOpen: boolean,
  anyOpen: boolean,
  focusOnOpen: RefObject<HTMLElement | null>,
  restoreTo: RefObject<HTMLButtonElement | null>,
): void {
  const wasOpen = useRef(isOpen);
  useEffect(() => {
    if (isOpen && !wasOpen.current) {
      focusOnOpen.current?.focus();
    } else if (!isOpen && wasOpen.current && !anyOpen) {
      const t = restoreTo.current;
      if (t && t.isConnected) t.focus();
    }
    wasOpen.current = isOpen;
  }, [isOpen, anyOpen, focusOnOpen, restoreTo]);
}

/**
 * Which top-level HUD menu is open (declutter pass). Only ONE is open at a time so the world stays calm
 * — opening one closes the others, mirroring the "one panel at a time" rule the guild slide-over already
 * follows. `null` = everything tucked away (the default, minimal state).
 *   • 'guild'  — Quests · Achievements · Leaderboards · Ship a release
 *   • 'me'     — Wardrobe · Presence flair · Photo mode · Log out
 *   • 'world'  — World theme (owner) · Invite teammate (admin)
 *   • 'people' — the existing People-nearby / voice-block panel
 */
// The two top-level disclosures that remain after the navigation declutter: the single main (burger)
// menu — the one home for Account / Quests & Achievements / Store / Leaderboard / Invite / Log out —
// and the live People-nearby roster. Guild / Me / World were folded into `main`.
type HudMenu = 'main' | 'people' | null;

/**
 * A discoverable top-level HUD menu, modelled as an ARIA DISCLOSURE (not a menu): a labelled toggle
 * button + a collapsed popover that follows the SAME accessible pattern the GuildPanel `Panel` /
 * PeoplePanel already use — focus-on-open (lands inside the popover), restore-to-opener on close, and
 * Esc-to-close. It's a disclosure, NOT role="menu", because the contents are Tab-navigated (no arrow-key
 * menu model) and hold content invalid for a menu — section labels, embedded role="group" pickers, the
 * Ship-release text input, the People panel. Deliberately NOT a focus-trap: like the rest of the HUD
 * these are non-blocking popovers beside a still-interactive world. One menu open at a time is enforced
 * by the parent (opening one closes the rest), keeping the screen quiet.
 *
 * Accessibility:
 *   • The toggle carries an explicit accessible NAME (`label`) + aria-expanded + aria-haspopup="true"
 *     (a generic "opens a popover" hint, NOT "menu" — this is a disclosure).
 *   • The popover is a labelled role="group" (aria-label = the menu name), tabIndex=-1 so it's a
 *     programmatic focus landing spot (and the Esc handler's host) without joining the Tab order; its
 *     inner controls are plain Tab-reachable buttons/inputs that keep their own visible focus rings.
 *   • Dual-coded toggle (icon + text), never colour/emoji-only; the active menu is shown via
 *     aria-expanded AND a highlighted button (not colour alone).
 *   • Reduced-motion: the popover entrance flattens to a no-op (info identical either way).
 */
function HudMenu({
  open,
  label,
  icon,
  badge,
  toggleRef: externalToggleRef,
  onToggle,
  onClose,
  children,
}: {
  open: boolean;
  /** The toggle's accessible name (e2e + SR target), e.g. "Guild menu". */
  label: string;
  icon: string;
  /** An optional small count badge on the toggle (e.g. blocked-voice count) — decorative, aria-hidden. */
  badge?: number;
  /**
   * Optional ref onto the always-mounted toggle BUTTON (FIX 1). The parent uses this to restore focus to
   * a stable element when an inner action swaps the whole render tree away (e.g. photo mode unmounts the
   * Me-menu popover, so the in-popover item it opened from is gone — the toggle button is not).
   */
  toggleRef?: RefObject<HTMLButtonElement | null>;
  /** Flip this menu open/closed (the parent closes the others so only one is open at a time). */
  onToggle: () => void;
  /** Close this menu (Esc / a child action) — restores focus to the toggle. */
  onClose: () => void;
  children: ReactNode;
}): JSX.Element {
  const reduceMotion = useReducedMotion();
  // Phones get a thumb-reachable bottom-SHEET instead of a top-right popover that would open above the
  // fold and off the edge. Same disclosure contract either way (toggle + role="group" + label + focus +
  // Esc) — only the positioning/animation changes, so e2e (which runs at a desktop viewport) is untouched.
  // `isCompact` (< md / 768px) is the phone tier.
  const { isCompact: isPhone } = useViewport();
  const internalToggleRef = useRef<HTMLButtonElement>(null);
  // Use the parent-supplied ref when given (FIX 1 stable restore target), else our own.
  const toggleRef = externalToggleRef ?? internalToggleRef;
  const popoverRef = useRef<HTMLDivElement>(null);

  // Focus-on-open + restore-on-close (WCAG 2.4.3), mirroring GuildPanel's `Panel`. On open we pull focus
  // into the popover so the keyboard/SR user lands in context and Esc works; on close we hand focus back
  // to the toggle. Keyed on `open` so it runs cleanly each time the menu opens.
  useEffect(() => {
    if (!open) return undefined;
    const toggle = toggleRef.current;
    popoverRef.current?.focus();
    return () => {
      if (toggle && toggle.isConnected && typeof toggle.focus === 'function') toggle.focus();
    };
    // `toggleRef` is a stable ref object (the parent's or our own) — safe to depend on; keyed on `open`.
  }, [open, toggleRef]);

  const motionProps = reduceMotion
    ? {}
    : isPhone
      ? {
          initial: { opacity: 0, y: 24 },
          animate: { opacity: 1, y: 0 },
          exit: { opacity: 0, y: 24 },
          transition: { duration: 0.22, ease: 'easeOut' as const },
        }
      : {
          initial: { opacity: 0, y: -8 },
          animate: { opacity: 1, y: 0 },
          exit: { opacity: 0, y: -8 },
          transition: { duration: 0.18, ease: 'easeOut' as const },
        };

  // The popover body. Identical accessible structure on every device — only the box's position changes:
  // a top-right anchored card on desktop/tablet, a full-width bottom-sheet on phones.
  const popoverClass = isPhone
    ? 'fixed inset-x-0 bottom-0 z-50 flex max-h-[85dvh] flex-col gap-2 overflow-y-auto rounded-t-3xl border-t border-white/10 bg-slate-950/90 p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] shadow-2xl outline-none backdrop-blur-xl'
    : 'absolute right-0 top-12 z-20 flex max-h-[min(70vh,32rem)] w-[min(18rem,calc(100vw-1.5rem))] flex-col gap-2 overflow-y-auto rounded-2xl border border-white/10 bg-slate-950/85 p-3 shadow-2xl outline-none backdrop-blur-xl';

  const popover = (
    <>
      {/* Phone: a tap-to-dismiss scrim behind the sheet (desktop popovers stay scrim-less so the
          world behind a small menu stays interactive). aria-hidden — it's a pointer affordance only. */}
      {isPhone && (
        <motion.div
          key="scrim"
          initial={reduceMotion ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          aria-hidden="true"
          onClick={onClose}
          className="fixed inset-0 z-50 bg-black/40"
        />
      )}
      <motion.div
        key="menu"
        ref={popoverRef}
        {...motionProps}
        role="group"
        aria-label={label}
        tabIndex={-1}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.stopPropagation();
            onClose();
          }
        }}
        className={popoverClass}
      >
        {/* Phone sheets get a grab-handle + a header (label + close) so the far-away toggle isn't the
            only way out. Desktop keeps the bare popover — its toggle sits right above it. */}
        {isPhone && (
          <div className="-mx-3 -mt-3 mb-1 flex flex-col gap-2 px-3 pt-2">
            <span aria-hidden="true" className="mx-auto h-1 w-10 rounded-full bg-white/20" />
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-semibold text-white/85">
                {label.replace(/ menu$/i, '')}
              </span>
              <button
                type="button"
                onClick={onClose}
                aria-label={`Close ${label}`}
                className="rounded-lg border border-white/10 bg-white/5 px-2 py-1 text-sm text-white/80 transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/50"
              >
                <span aria-hidden="true">✕</span>
              </button>
            </div>
          </div>
        )}
        {children}
      </motion.div>
    </>
  );

  // On phones the sheet must clear the rest of the HUD chrome (the bottom action bar, etc.). Those docks
  // sit in their own `z-20` stacking contexts, and this menu lives inside the top-right cluster's own
  // `z-20` context — so a nested `z-50` alone can't rise above a sibling dock. Portalling to <body> lifts
  // the whole AnimatePresence out of every local stacking context so the sheet reliably covers the
  // screen. The desktop popover stays inline (anchored under its toggle via the `relative` parent). Same
  // DOM/aria either way — only the mount point differs. (Portal the AnimatePresence, not its result, so
  // it can still track the keyed motion children for enter/exit.)
  const usePortal = isPhone && typeof document !== 'undefined';
  const presence = <AnimatePresence>{open && popover}</AnimatePresence>;

  return (
    <div className="pointer-events-auto relative">
      <button
        ref={toggleRef}
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-haspopup="true"
        aria-label={label}
        title={label}
        className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/50 ${
          open
            ? 'border-amber-300/50 bg-amber-400/25 text-amber-100'
            : 'border-white/10 bg-white/5 text-white hover:bg-white/10'
        }`}
      >
        <span aria-hidden="true" className="text-base">{icon}</span>
        {/* Icon-only navigation (UI redesign): no visible text label at any width — the corner reads as a
            calm row of icons over the world, not an admin panel. The accessible name lives on the button's
            aria-label (stable for screen readers + e2e); the phone bottom-sheet shows the name in its header. */}
        {typeof badge === 'number' && badge > 0 && (
          <span
            aria-hidden="true"
            className="rounded-full bg-white/15 px-1.5 text-[10px] font-bold text-white/80"
          >
            {badge}
          </span>
        )}
      </button>
      {usePortal ? createPortal(presence, document.body) : presence}
    </div>
  );
}

/** Lifecycle of the voice subsystem mirrored from the VoiceClient (kept in sync via OfficePage). */
export type VoiceHudStatus =
  | 'idle'
  | 'requesting'
  | 'live'
  | 'denied'
  | 'unsupported'
  | 'error';

/**
 * One citizen I've blocked (M3.4). Tracked by STABLE userId so the block survives them reconnecting
 * (a fresh sessionId no longer orphans the entry). `sessionId` is their CURRENT session resolved from
 * the roster, or null when they're offline — in which case `online` is false and Unblock is disabled
 * (we can't lift a block without a live session to send it to). Name comes from their current roster
 * entry while present, and falls back to a remembered/placeholder name once they've left.
 */
export interface BlockedPeer {
  userId: string;
  name: string;
  /** Current session id (for the UNBLOCK wire message), or null when the citizen is offline. */
  sessionId: string | null;
  /** Whether the blocked citizen is currently present in the room (gates the Unblock control). */
  online: boolean;
}

/**
 * One participant in the session-roster list (the "who's in the office, and where" view). Carries their
 * world position ({@link RosterOccupant} shape: `id` = sessionId, `x`/`y`) so the pure grouper can place
 * them in a room, plus the bits the row renders: name, look (species/colour), whether it's me, and their
 * Discord-style audio-device state — `voiceOn` (can HEAR the room) and `micMuted` (mic deliberately off).
 */
export interface RosterMember {
  /** The citizen's CURRENT Colyseus session id (also the {@link RosterOccupant} `id`). */
  id: string;
  /** The citizen's STABLE userId — keys their personal voice trim (survives their reconnect). */
  userId: string;
  x: number;
  y: number;
  name: string;
  species: string;
  duckColor: string;
  /** True for the local player's own row (rendered as "you", never blockable). */
  isSelf: boolean;
  /** Their voice is live, so they can HEAR the room (🎧). False ⇒ not in voice / can't hear. */
  voiceOn: boolean;
  /** Their mic is deliberately muted (🎤✗) — only meaningful while {@link voiceOn}. */
  micMuted: boolean;
}

/** One room section of the participant list: its zone, its members, and whether it's the viewer's room. */
export type RosterSection = RoomGroup<RosterMember>;

/**
 * One line in the "recent chatter" strip (M7a "Socialize"). Mirrors an incoming SAID/REACTED broadcast —
 * STRUCTURED (not pre-joined) so the panel can render the speaker's name BOLD + tinted with their CHARACTER
 * colour, auto-linkify URLs in a spoken message, and offer per-line copy, while screen readers still read a
 * natural "{Name}: {text}" / "{Name} reacted {emoji}" (the Pixi bubble/emoji are invisible to AT). EPHEMERAL
 * — never persisted; only the last few are kept in memory.
 */
export interface ChatLine {
  /** Stable id (monotonic) so React can key the list without persisting anything. */
  id: number;
  /**
   * How to render `text`:
   *  - `'say'`    — a spoken message → render "Name: <body>", auto-linkify URLs in the body, copyable.
   *  - `'action'` — "Name <did something>" (reacted / played a sound …) → name styled, body verbatim.
   *  - `'system'` — a nameless note (🔔 …/✊ …) → plain.
   */
  kind: 'say' | 'action' | 'system';
  /** The speaker's display name ("You"/"Ada"); omitted for a `'system'` note. */
  name?: string;
  /** The speaker's character colour as a readable CSS colour (set together with `name`). */
  color?: string;
  /** `say` → the raw message; `action` → the predicate ("reacted 👍"); `system` → the whole note. */
  text: string;
  /**
   * For a "played a sound" action line: the sound id, so the row can offer a 🔊 replay button that
   * re-plays this meme FOR THE WHOLE ROOM — it routes through the soundboard broadcast path
   * (server-validated, rate-limited, proximity-gated), so a replay is a real room event, not a
   * private encore (everyone in range hears it, and it produces its own fresh chat line).
   */
  soundId?: SoundReactionId;
}

/**
 * A pending Rock-Paper-Scissors invite (M7b-2 "Play Together", emotion: Tradition). Mirrors the
 * server's RPS_INVITE so the HUD can pop a friendly, focus-managed prompt — "{fromName} challenged you
 * to Rock-Paper-Scissors!" — with the three choice buttons + Decline. Server-authoritative: the matchId
 * comes from the server (the client never invents one).
 */
export interface RpsInvitePrompt {
  matchId: string;
  fromSessionId: string;
  fromName: string;
}

/**
 * The local player's view of the RPS match they're currently in (M7b-2). Drives the choose / waiting
 * states. Purely client-side presentation — the SERVER owns the match + the outcome.
 *   • 'choosing' — I've been shown the three choices and haven't picked yet (an open invite I'm in).
 *   • 'waiting'  — I've picked; we're waiting for my opponent to pick (or the server to resolve).
 * `myChoice` is set once I pick (for the "you played ✊" waiting copy).
 */
export interface RpsMatchView {
  matchId: string;
  opponentName: string;
  phase: 'choosing' | 'waiting';
  myChoice?: RpsChoice;
}

/**
 * The last finished RPS match (M7b-2) — a warm, transient result card. `outcome` is from MY point of
 * view (win = I won). `yourChoice`/`theirChoice` are present only when both actually played
 * (win/lose/draw); 'declined'/'expired' have none and are handled gently.
 */
export interface RpsResultView {
  matchId: string;
  outcome: RpsOutcome;
  opponentName: string;
  yourChoice?: RpsChoice;
  theirChoice?: RpsChoice;
}

/** Which minigame a Challenge tap starts (the Challenge button is now a tiny 2-game picker). */
export type ChallengeGame = 'rps' | 'bark';

/**
 * A pending Bark Battle invite (Challenge game #2). Mirrors the server's BARK_INVITE so the HUD can
 * pop the accept/decline prompt — "{fromName} challenges you to a Bark Battle!". Server-authoritative:
 * the matchId comes from the server (the client never invents one).
 */
export interface BarkInvitePrompt {
  matchId: string;
  fromSessionId: string;
  fromName: string;
}

/**
 * The local player's view of the Bark Battle they're in (Challenge game #2). Purely client-side
 * presentation — the SERVER owns the match, the window, and the only real totals.
 *   • 'waiting'   — my challenge is out (or I just accepted); nothing to do yet.
 *   • 'countdown' — accepted: the shared 3-2-1 runs (`countdownMs` from BARK_START).
 *   • 'battle'    — the window is OPEN: the mic sampler streams and the scoreboard runs. The 10 Hz
 *     scores live in the isolated bark-score store (perf-audit R1), NOT here — this view only holds
 *     the slow-changing identity, so the HUD doesn't re-render ten times a second.
 */
export interface BarkMatchView {
  matchId: string;
  opponentName: string;
  phase: 'waiting' | 'countdown' | 'battle';
  /** Shared countdown length (ms) from BARK_START — drives the 3-2-1 display. */
  countdownMs: number;
  /** Barking-window length (ms) from BARK_START — seeds the scoreboard clock. */
  durationMs: number;
}

/**
 * The last finished Bark Battle — a warm, transient result card. `outcome` is from MY point of view
 * (win = I out-barked them). Scores are present only when the window was actually played.
 */
export interface BarkResultView {
  matchId: string;
  outcome: BarkOutcome;
  opponentName: string;
  yourScore?: number;
  theirScore?: number;
}

interface HudProps {
  user: AuthenticatedUser;
  playerCount: number;
  /**
   * Live connection health (M3.5b). `connected` = invisible (normal). `reconnecting` = a gentle,
   * non-blocking "Reconnecting…" indicator while the same seat is transparently re-claimed. `lost` =
   * a calm "Connection lost — reload" affordance after the grace window elapses. Never blocks the world.
   */
  netStatus: ConnectionStatus;
  progression?: ProgressionStatsDto;
  guild?: GuildInfluenceDto;
  notice?: string | null;
  voiceStatus: VoiceHudStatus;
  voiceMuted: boolean;
  /** Local webcam lifecycle ("show your face", ADR-0030). Drives the camera button + self-preview. */
  cameraStatus: CameraStatus;
  /** The room the local player is standing in (null = open floor). Drives the zone chip. */
  currentZone: CurrentZone | null;
  /** Push-to-talk mode on/off (M3.3). When on, mic is muted unless V is held. */
  pttEnabled: boolean;
  // Mic input level (M3.3) is no longer a HUD prop: the ~8 Hz meter reads the isolated
  // mic-level store directly (perf-audit R1), so it never re-renders the HUD.
  /** Available microphones for the device picker (M3.3). Empty ⇒ picker hidden. */
  micDevices: MicDevice[];
  /** The deviceId currently in use (or null/'' for the system default). */
  currentDeviceId: string | null;
  /** Available audio-output devices (speakers/headphones) for the output picker. Empty ⇒ picker hidden
   *  (no devices yet, or output selection unsupported on this browser). */
  speakerDevices: MicDevice[];
  /** The output deviceId currently selected (or null/'' for the system default). */
  currentSinkId: string | null;
  /**
   * The WHOLE session grouped by room — the participant list. Every citizen appears under the room their
   * position falls in; the viewer's own room is marked current + pinned first. Recomputed live as ducks
   * join, leave, move between rooms, or change their voice state.
   */
  roomSections: RosterSection[];
  /**
   * Session ids I can currently HEAR (already excludes blocked) — the rows that get an inline voice-Block
   * control. Blocking only makes sense for someone you can actually hear, so other rows omit it.
   */
  audibleSessionIds: Set<string>;
  /** The citizens I've BLOCKED (drives the "Blocked" section; M3.4). Self-protection, never punishment. */
  blockedPeers: BlockedPeer[];
  /**
   * A polite screen-reader announcement for the last voice block/unblock (M3.4, FIX 2, WCAG 4.1.3).
   * Rendered into a visually-hidden role="status" aria-live region so the (silent, private) action is
   * still conveyed to assistive tech. Empty string = nothing to announce.
   */
  voiceAnnouncement: string;
  /** The local player's ELECTED presence flair (a chosen badge, never inferred — M4). */
  myPresence: PresenceFlavor;
  /** Nearest interactable object to the local duck, or null when none is in range (M4). */
  nearestObject: InteractiveObject | null;
  /** Nearest EMPTY seat to the local duck, or null when none is in range / I'm seated (Task 2). */
  nearestSeat: Seat | null;
  /** Nearest ENTRYWAY front-door action (ring the bell / knock / open the door) in range, or null (ADR-0022). */
  nearestEntryAction: EntryActionView | null;
  /** Nearest interior-door action (open/close the doorway in reach) in range, or null (Room Doors). */
  nearestDoorAction: DoorActionView | null;
  /** Whether the local player is the HOST (workspace OWNER) — gates the "Doors" panel (open/close any door). */
  isHost: boolean;
  /** Live door state for the host panel: the closed interior-door ids + whether the entry door is open. */
  doorState: { closed: string[]; entryOpen: boolean; held: string[] };
  /** The seat the local duck is sitting on, or '' when standing (Task 2) — drives "Stand up". */
  mySeatedAt: string;
  /**
   * Whether the local duck is seated at a WORKSTATION desk seat right now (gamified-XP redesign). When
   * true the HUD surfaces a repeatable "Work" prompt that sends a desk-work beat (+1 XP on a server
   * cooldown). Derived from `mySeatedAt` + `isWorkstationSeat` upstream so the HUD stays presentational.
   */
  atWorkstation: boolean;
  /** The fun prop the local duck is holding, or '' for empty hands (Task 2) — drives the HOLD picker. */
  myHeldItem: string;
  /**
   * Live MEETING-ROOM quest progress (gamified-XP redesign), synced room-wide. When `phase !== 'idle'`
   * the HUD shows a shared "meeting forming / in session" progress bar so every client celebrates the
   * same co-op moment. Purely a celebratory cue, never a metric.
   */
  meeting: { seated: number; phase: 'idle' | 'standup' | 'meeting'; elapsedSec: number; targetSec: number };
  /** Shared "Living World" day clock [0,1) — drives the dual-coded time-of-day indicator (M4.2). */
  timeOfDay: number;
  /** Shared "Living World" weather — drives the dual-coded weather indicator (M4.2). */
  weather: Weather;
  /**
   * The SHARED random world event active right now (M7b "Play Together"), or 'none'. Drives the
   * friendly, non-blocking event banner + the polite screen-reader announcement when one STARTS.
   */
  worldEvent: WorldEventKind;
  /**
   * The guild's WORLD THEME (M8 "Guilds at Scale") everyone shares right now — the SAME engine, re-skinned
   * per guild. Drives the calm world-NAME chip (so you can tell which world you're in) and the owner
   * picker's current selection. Shared + guild-wide; an owner's switch updates it for everyone.
   */
  worldThemeId: WorldThemeId;
  /** Which M5 guild panel is open (Quests / Achievements / Leaderboards), or null. */
  guildPanel: GuildPanelTab;
  /**
   * The last few lines of social chatter (M7a) — incoming speech + reactions, already formatted for the
   * accessible aria-live strip. Ephemeral (never persisted); the parent keeps only ~the last few.
   */
  chatLines: ChatLine[];
  /**
   * A pending RPS invite (M7b-2 "Play Together"), or null. When set, the HUD pops a friendly, focus-
   * managed dialog with the three choice buttons + Decline. Server-authoritative — only present after a
   * real RPS_INVITE from the server.
   */
  rpsInvite: RpsInvitePrompt | null;
  /** The local player's current RPS match view (choosing / waiting), or null when not in a match. */
  rpsMatch: RpsMatchView | null;
  /** The last finished RPS match for the warm transient result card, or null. */
  rpsResult: RpsResultView | null;
  /** A pending Bark Battle invite (Challenge game #2), or null. Pops the accept/decline prompt. */
  barkInvite: BarkInvitePrompt | null;
  /** The local player's current Bark Battle view (waiting / countdown / battle), or null. */
  barkMatch: BarkMatchView | null;
  /** The last finished Bark Battle for the warm transient result card, or null. */
  barkResult: BarkResultView | null;
  onEmote: (animation: Animation) => void;
  /** Open (or toggle closed) a guild panel from a HUD toggle button (M5). */
  onToggleGuildPanel: (tab: Exclude<GuildPanelTab, null>) => void;
  /** Close any open guild panel (M5). */
  onCloseGuildPanel: () => void;
  /** Fire the guild-wide celebration after a release ships (reuses CELEBRATE broadcast) (M5). */
  onShipRelease: (name: string) => void;
  /** Surface a transient celebratory notice from a panel (reuses flashNotice) (M5). */
  onNotice: (message: string) => void;
  /** Enable voice (must be a user gesture — getUserMedia + AudioContext autoplay). */
  onEnableVoice: () => void;
  /** Toggle mute when voice is live. */
  onToggleMute: () => void;
  /**
   * Toggle the local webcam on/off ("show your face", ADR-0030). On the FIRST enable, OfficePage shows a
   * one-time primer before the browser permission prompt; thereafter this is a direct toggle. Always a
   * one-click instant kill switch when on — never a confirm dialog.
   */
  onToggleCamera: () => void;
  /** Toggle push-to-talk mode (M3.3). */
  onTogglePtt: () => void;
  /** Switch the active microphone (M3.3). */
  onSelectMic: (deviceId: string) => void;
  /** Switch where voice plays back — speaker / headphones (audio output device). */
  onSelectSpeaker: (deviceId: string) => void;
  /** Block a citizen's voice — self-protection (M3.4). The server severs the audio both ways. */
  onBlockCitizen: (sessionId: string) => void;
  /** Undo a voice block (M3.4). */
  onUnblockCitizen: (sessionId: string) => void;
  /**
   * Owner-only (M8 "Guilds at Scale"): set the guild's WORLD THEME — the SAME engine re-skins for the
   * whole guild. Cosmetic + guild-wide; the gateway persists it and the realtime broadcasts the change
   * so everyone present re-skins live. Gated on the owner in the HUD (non-owners never see the control).
   */
  onSetWorldTheme: (theme: WorldThemeId) => void;
  /** Elect (or clear, with 'none') the presence flair shown over the local duck (M4). */
  onSetPresence: (flavor: PresenceFlavor) => void;
  /** Use the nearest object — the accessible alternative to the in-world E keypress (M4 a11y). */
  onInteractNearest: () => void;
  /** Perform the nearest entry front-door action (ring/knock/open) — accessible alternative to E (ADR-0022). */
  onEntryAction: () => void;
  /** Open/close the nearest interior door — accessible alternative to the in-world E keypress (Room Doors). */
  onDoorActionNearest: () => void;
  /** Host-only: open/close ANY door from the Doors panel (interior doorway id, or {@link ENTRY_DOORWAY_ID}). */
  onHostToggleDoor: (doorwayId: string, open: boolean) => void;
  /** The RESOLVED level's interior doorways (Phase 3) — the Doors panel lists THESE, not the office globals,
   *  so the host panel matches whichever world (office or castle) is loaded. */
  doorways: readonly Doorway[];
  /**
   * Sit on the nearest seat / stand up (Task 2 "interactive SIT") — the accessible alternative to the
   * in-world E keypress. A server-authoritative toggle: seated → stand, else sit on the nearest seat.
   */
  onSitNearest: () => void;
  /**
   * Do a beat of WORK while seated at a desk (gamified-XP redesign). Sends ClientMessage.WORK; the SERVER
   * re-checks seated-at-a-desk + the cooldown and attests the +1 XP, which lands back as a QUEST_FIRED
   * toast. Repeatable — the button stays available while seated at a workstation.
   */
  onWork: () => void;
  /** Hold (or empty, with '') a fun prop in hand (Task 2 "HOLD ITEMS"). Cosmetic-only. */
  onHoldItem: (item: string) => void;
  /** Broadcast the freshly-equipped cosmetic look over the realtime room so everyone sees it (M6). */
  onLookChange: (equipped: EquippedCosmetics) => void;
  /** Say a short line (M7a) — pops a speech bubble over my duck (and fans out to everyone). */
  onSay: (text: string) => void;
  /** Send a quick emoji reaction (M7a) from the closed REACTIONS set — floats up from my duck. */
  onReact: (reaction: Reaction) => void;
  /**
   * Play a short, shared SOUND reaction (Task 6b "Sound reactions") from the closed SOUND_REACTIONS
   * catalog — the sonic cousin of {@link onReact}: everyone NEARBY hears it (incl. me, off the server's
   * echo). Pure joy/belonging; nothing persisted, no progression effect.
   */
  onSoundReact: (sound: SoundReactionId) => void;
  /** Warm the decode cache for a sound on hover/focus so its first play is gapless (pure client perf). */
  onSoundPrefetch?: (sound: SoundReactionId) => void;
  /**
   * Challenge the NEAREST other duck to the picked minigame (M7b-2 RPS, or the Bark Battle). The parent
   * picks the target via the same proximity logic give-reputation uses (the realtime owns positions +
   * re-checks). A kind "want to play?" tap — playing, win or lose, is the positive action; the point is
   * the moment together.
   */
  onChallengeNearest: (game: ChallengeGame) => void;
  /** Submit my choice in the active RPS match (M7b-2). */
  onRpsChoose: (matchId: string, choice: RpsChoice) => void;
  /** Decline an RPS invite (M7b-2) — a gentle "no thanks", never a penalty. */
  onRpsDecline: (matchId: string) => void;
  /** Dismiss the transient RPS result card (M7b-2) — the "gg!" was seen. */
  onDismissRpsResult: () => void;
  /** Accept a Bark Battle invite (Challenge game #2) — the server starts the shared countdown. */
  onBarkAccept: (matchId: string) => void;
  /** Decline / back out of a Bark Battle — a gentle "no thanks", never a penalty. */
  onBarkDecline: (matchId: string) => void;
  /** Dismiss the transient Bark Battle result card — the "gg!" was seen. */
  onDismissBarkResult: () => void;
  /**
   * Capture the world as a PNG (M7a photo mode). Fire-and-forget — OfficePage owns the engine/canvas,
   * runs the async extract, and surfaces a gentle notice if it couldn't capture. The HUD only triggers
   * it from the photo-mode capture button.
   */
  onCapturePhoto: () => void;
  /**
   * Furniture Store: the player picked "Buy & Place" for a catalog item. OfficePage arms the engine's
   * placement ghost (the Store closes itself); clicking the world confirms the buy & place. The HUD only
   * forwards the chosen item from the Store's Furniture tab.
   */
  onArmFurniture: (item: FurnitureItem) => void;
  onLogout: () => void;
}

function StatChip({ icon, label, value }: { icon: string; label: string; value: string }): JSX.Element {
  return (
    <div className="flex items-center gap-2 rounded-xl bg-white/5 px-3 py-1.5">
      <span className="text-base">{icon}</span>
      <div className="leading-tight">
        <div className="text-sm font-bold text-white">{value}</div>
        <div className="text-[10px] uppercase tracking-wide text-white/40">{label}</div>
      </div>
    </div>
  );
}

/**
 * The coin wallet chip (M6 "Express Yourself"). A small, calm "🪙 N" — coins are EARNED from positive
 * actions and only ever buy cosmetics (never pay-to-win). Dual-coded (icon + number) with a full
 * accessible label so it's not colour/emoji-only. Reads `coins` off the gateway-attested progression.
 */
function CoinChip({ coins }: { coins?: number }): JSX.Element {
  const value = typeof coins === 'number' ? String(coins) : '—';
  return (
    <span
      aria-label={typeof coins === 'number' ? `${coins} coins` : 'Coins'}
      title="Coins — earned from kind, positive actions. They only ever buy looks, never an advantage."
      className="flex items-center gap-1.5 rounded-xl border border-amber-300/30 bg-amber-400/15 px-3 py-2 text-sm font-semibold text-amber-100"
    >
      <span aria-hidden="true">🪙</span>
      <span>{value}</span>
    </span>
  );
}

/**
 * A single row inside a HUD menu (disclosure) popover — a calm, full-width, dual-coded (icon + label)
 * PLAIN button (no role="menuitem"; the popover is a role="group" disclosure, not an ARIA menu, so its
 * children are ordinary Tab-reachable buttons). Used for the actions that OPEN another surface (a
 * slide-over) or fire a one-shot action. Keeps its accessible NAME UNCHANGED (the e2e keys off it) and
 * its own visible focus ring; `active` reflects an already-open surface via aria-pressed (valid on a
 * <button>) AND a highlight.
 *
 * The open/active state is conveyed BEYOND colour (WCAG 1.4.1) on TWO independent channels, so neither
 * SR nor colour-blind users depend on the amber tint:
 *   • Screen readers: aria-pressed=true ("pressed") — a non-visual, non-colour state cue.
 *   • Sighted/colour-blind: a leading "●" glyph + a visible "(open)" suffix (both aria-hidden so they
 *     don't alter the accessible name — keeping it stable for the e2e).
 * `tone` tints the quiet, neutral rows (e.g. Log out).
 */
function MenuItem({
  icon,
  label,
  ariaLabel,
  title,
  active,
  tone = 'default',
  buttonRef,
  onClick,
}: {
  icon: string;
  label: string;
  /** Explicit accessible name (defaults to `label`) — the stable e2e / SR target. */
  ariaLabel?: string;
  title?: string;
  active?: boolean;
  /** Visual tone: default · quiet (muted) · highlight (the social/invite call-to-action) · danger (log out). */
  tone?: 'default' | 'quiet' | 'highlight' | 'danger';
  buttonRef?: RefObject<HTMLButtonElement | null>;
  onClick: () => void;
}): JSX.Element {
  const restClass =
    tone === 'danger'
      ? 'border-red-300/40 bg-red-400/10 text-red-100 hover:bg-red-400/20'
      : tone === 'highlight'
        ? 'border-emerald-300/45 bg-emerald-400/15 text-emerald-50 hover:bg-emerald-400/25'
        : tone === 'quiet'
          ? 'border-white/10 bg-white/5 text-white/75 hover:bg-white/10'
          : 'border-white/10 bg-white/5 text-white hover:bg-white/10';
  return (
    <button
      ref={buttonRef}
      type="button"
      onClick={onClick}
      aria-pressed={typeof active === 'boolean' ? active : undefined}
      aria-label={ariaLabel ?? label}
      title={title ?? ariaLabel ?? label}
      className={`flex w-full items-center gap-2.5 rounded-xl border px-3 py-2 text-left text-sm font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/50 ${
        active ? 'border-amber-300/50 bg-amber-400/25 text-amber-100' : restClass
      }`}
    >
      <span aria-hidden="true" className="text-base">{icon}</span>
      <span className="min-w-0 flex-1 truncate">
        {/* Non-colour active cue for sighted/colour-blind users: a leading dot before the open row's
            label. aria-hidden so it doesn't change the accessible name (the e2e target) — SR users get
            the state from aria-pressed instead. */}
        {active && <span aria-hidden="true" className="mr-1 text-amber-200">●</span>}
        {label}
        {/* …and a plain-text "(open)" suffix — a second, words-not-colour signal of the open panel. */}
        {active && <span aria-hidden="true" className="text-amber-200/80"> (open)</span>}
      </span>
    </button>
  );
}

/** A small uppercase section heading inside a HUD menu popover (groups related rows calmly). */
function MenuSectionLabel({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className="px-1 pt-1 text-[10px] font-semibold uppercase tracking-wide text-white/40">
      {children}
    </div>
  );
}

/** The mic button's look + behaviour for each voice lifecycle state (ADR-0009: never a hidden mic). */
function MicButton({
  status,
  muted,
  pttEnabled,
  onEnableVoice,
  onToggleMute,
  devicesOpen,
  onToggleDevices,
}: {
  status: VoiceHudStatus;
  muted: boolean;
  /** Push-to-talk mode (M3.3). When on, the mic is gated by the V key — NOT the manual mute toggle. */
  pttEnabled: boolean;
  onEnableVoice: () => void;
  onToggleMute: () => void;
  /** Whether the device chooser is expanded (drives the chevron's flip + aria-expanded). */
  devicesOpen?: boolean;
  /** Toggle the mic/speaker device chooser. When omitted, the chevron is hidden (pre-`live`). */
  onToggleDevices?: () => void;
}): JSX.Element {
  if (status === 'unsupported') {
    return (
      <span
        title="Your browser can’t do spatial voice"
        className="rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm font-medium text-white/40"
      >
        🔇 Voice unavailable
      </span>
    );
  }

  if (status === 'idle' || status === 'requesting' || status === 'denied' || status === 'error') {
    const label =
      status === 'requesting'
        ? 'Enabling…'
        : status === 'denied'
          ? 'Allow mic'
          : status === 'error'
            ? 'Retry voice'
            : 'Enable voice';
    const title =
      status === 'denied'
        ? 'Microphone permission was blocked. Click to ask again (or enable it in your browser’s site settings).'
        : 'Turn on proximity voice — talk to nearby ducks. A ring shows when anyone (including you) is speaking.';
    return (
      <button
        onClick={onEnableVoice}
        disabled={status === 'requesting'}
        title={title}
        className={`rounded-xl border px-3 py-2 text-sm font-medium transition disabled:opacity-60 ${
          status === 'denied' || status === 'error'
            ? 'border-amber-300/40 bg-amber-400/15 text-amber-100 hover:bg-amber-400/25'
            : 'border-white/10 bg-white/5 text-white hover:bg-white/10'
        }`}
      >
        {/* Icon-only on phones (the label is `sr-only` there, NOT removed — it stays in the DOM + a11y
            tree, so the accessible name the voice e2e keys off is unchanged at every width). */}
        🎤 <span className="sr-only">{label}</span>
      </button>
    );
  }

  // status === 'live'
  return (
    <div className="relative">
      {pttEnabled ? (
        // Push-to-talk owns the mic gate here — the V key, NOT a manual mute. Render an INERT indicator
        // (never the mute toggle) so the player can't silently latch a deliberate-mute behind the PTT
        // gate — that latch used to strand the mic muted even while holding V and after leaving PTT (the
        // "push-to-talk kills the mic" bug). `muted` is the EFFECTIVE gate: amber 🎙️ when idle (hold V),
        // emerald the instant V is held. Turning PTT off (Voice settings) restores the mute toggle below.
        <span
          role="img"
          aria-label={muted ? 'Push-to-talk on — hold V to talk' : 'Push-to-talk — talking'}
          title="Push-to-talk is on — hold V to talk. Turn it off in Voice settings for open-mic."
          className={`flex select-none items-center rounded-xl border px-3 py-2 text-sm font-medium ${
            muted
              ? 'border-amber-300/40 bg-amber-400/15 text-amber-100'
              : 'border-emerald-300/40 bg-emerald-400/15 text-emerald-100'
          }`}
        >
          {/* role="img" + aria-label names this for AT; the glyph is decorative (no extra sr-only text,
              which would double-announce). Dual-coded for sighted users via glyph + amber/emerald colour. */}
          <span aria-hidden="true">🎙️</span>
        </span>
      ) : (
        <button
          onClick={onToggleMute}
          title={muted ? 'You are muted — click to talk' : 'Mic live — click to mute'}
          className={`rounded-xl border px-3 py-2 text-sm font-medium transition ${
            muted
              ? 'border-red-300/40 bg-red-400/15 text-red-100 hover:bg-red-400/25'
              : 'border-emerald-300/40 bg-emerald-400/15 text-emerald-100 hover:bg-emerald-400/25'
          }`}
        >
          {muted ? (
            <>
              🔇 <span className="sr-only">Muted</span>
            </>
          ) : (
            <>
              🎙️ <span className="sr-only">Live</span>
            </>
          )}
        </button>
      )}
      {/* Google-Meet-style device chooser handle: a small chevron tucked into the button's bottom-left
          corner. Click to reveal the microphone + speaker pickers (collapsed by default). The glassy
          handle warms to the brand amber when open/hovered, matching the HUD's icon language. */}
      {onToggleDevices && (
        <button
          type="button"
          onClick={onToggleDevices}
          aria-label="Microphone and speaker settings"
          aria-expanded={devicesOpen ?? false}
          title="Choose microphone & speaker"
          className={`absolute -bottom-1.5 -left-1.5 flex h-5 w-5 items-center justify-center rounded-full border shadow-md backdrop-blur-sm transition focus:outline-none focus:ring-2 focus:ring-amber-300/60 ${
            devicesOpen
              ? 'border-amber-300/50 bg-amber-400/20 text-amber-100'
              : 'border-white/15 bg-slate-900/85 text-white/75 hover:border-amber-300/40 hover:text-amber-100'
          }`}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2.6}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            focusable="false"
            className={`h-3 w-3 transition-transform duration-200 ${devicesOpen ? '' : 'rotate-180'}`}
          >
            <path d="M6 15l6-6 6 6" />
          </svg>
        </button>
      )}
    </div>
  );
}

/**
 * Webcam toggle ("show your face", ADR-0030) — the twin of {@link MicButton}, sitting just to its left.
 * Mic = let them hear me · Camera = let them see me. Always visible so the LIVE state is glanceable and
 * the kill switch is one click from anywhere. Opt-in: default off (the unremarkable norm — no nag, no
 * shaming of camera-off). When on, a clear "Sharing" state with a gentle pulse, `aria-pressed` for AT.
 * Dual-coded (glyph + text + colour), so the state never relies on colour alone. Quietly hidden when the
 * device genuinely can't capture video — a person with no camera loses nothing.
 */
function CamButton({
  status,
  onToggleCamera,
}: {
  status: CameraStatus;
  onToggleCamera: () => void;
}): JSX.Element | null {
  if (status === 'unsupported') return null; // no camera → no button, no nag

  if (status === 'on') {
    return (
      <button
        type="button"
        onClick={onToggleCamera}
        aria-pressed={true}
        aria-label="Hide your face — you are sharing your camera with nearby ducks"
        title="Your face is shared with nearby ducks — click to hide"
        className="rounded-xl border border-amber-300/50 bg-amber-400/20 px-3 py-2 text-sm font-medium text-amber-100 transition hover:bg-amber-400/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 motion-safe:animate-pulse"
      >
        <span aria-hidden="true">📸</span> <span className="hidden">Sharing</span>
      </button>
    );
  }

  const requesting = status === 'requesting';
  const needsRetry = status === 'denied' || status === 'error';
  const label = requesting
    ? 'Starting…'
    : status === 'denied'
      ? 'Allow camera'
      : status === 'error'
        ? 'Retry camera'
        : 'Show face';
  const title =
    status === 'denied'
      ? 'Camera permission was blocked. Click to ask again (or enable it in your browser’s site settings).'
      : 'Show your real face to the ducks standing near you — nearby only, never recorded. Click to hide anytime.';
  return (
    <button
      type="button"
      onClick={onToggleCamera}
      disabled={requesting}
      aria-pressed={false}
      aria-label={requesting ? 'Starting your camera' : 'Show your face to nearby ducks'}
      title={title}
      className={`rounded-xl border px-3 py-2 text-sm font-medium transition disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 ${
        needsRetry
          ? 'border-amber-300/40 bg-amber-400/15 text-amber-100 hover:bg-amber-400/25'
          : 'border-white/10 bg-white/5 text-white hover:bg-white/10'
      }`}
    >
      <span aria-hidden="true">📷</span> <span className="hidden">{label}</span>
    </button>
  );
}


/**
 * Shared world-event banner (M7b "Play Together", emotion: Tradition). When the server flips a
 * spontaneous, SHARED event on (pizza / disco / fire_drill — the same one everyone sees), this shows a
 * friendly, NON-BLOCKING banner inviting people to gather: the contract's dual-coded copy (icon + TITLE
 * + a warm blurb). It's a celebration, never a task or a metric (Constitution Art. 1).
 *
 * Accessibility:
 *   • DUAL-CODED — icon AND text title AND blurb, never colour/emoji-alone; a full `aria-label` carries
 *     the whole "🍕 Pizza Party! Fresh pizza in the lounge…" sense to screen readers.
 *   • The visuals live in the canvas (invisible to AT), so a visually-hidden `role="status"`
 *     `aria-live="polite"` region ANNOUNCES the event when it STARTS (and only then — it doesn't re-read
 *     while it runs, and clears on end), so SR users hear about the gathering without an alarm.
 *   • Reduced-motion — the entrance slide/fade flattens to a no-op (the info is identical either way);
 *     nothing here pulses.
 * Renders nothing while `worldEvent` is 'none'.
 */
function WorldEventBanner({ worldEvent }: { worldEvent: WorldEventKind }): JSX.Element | null {
  const reduceMotion = useReducedMotion();
  // Announce ONLY on a START (a transition into a real event), never every render while it runs. Track
  // the previous kind; when it changes to a non-none event we set the live message, and clear it on end
  // (or change) so the region re-fires for the next event rather than staying "stuck" on the last one.
  const prevKind = useRef<WorldEventKind>(worldEvent);
  const [announcement, setAnnouncement] = useState('');
  useEffect(() => {
    if (prevKind.current === worldEvent) return;
    prevKind.current = worldEvent;
    if (worldEvent === 'none') {
      setAnnouncement('');
      return;
    }
    const { icon, title, blurb } = WORLD_EVENT_LABELS[worldEvent];
    // e.g. "🍕 Pizza Party! Fresh pizza in the lounge — grab a slice together." — heard once on start.
    setAnnouncement(`${icon} ${title} ${blurb}`);
  }, [worldEvent]);

  // The live region is ALWAYS mounted (even while empty) so a status change is announced politely.
  const live = (
    <span role="status" aria-live="polite" className="sr-only">
      {announcement}
    </span>
  );

  if (worldEvent === 'none') {
    // Keep the live region mounted so the "event ended" clear doesn't yank a node screen readers track.
    return live;
  }

  const { icon, title, blurb } = WORLD_EVENT_LABELS[worldEvent];
  const ariaLabel = `${title}. ${blurb}`;
  const enter = reduceMotion ? {} : { initial: { opacity: 0, y: -10 }, animate: { opacity: 1, y: 0 } };

  return (
    <>
      {live}
      <motion.div
        {...enter}
        // The visible banner stays OUT of the live region (the sr-only span above is the SOLE announcer)
        // so the title/blurb aren't double-read. aria-label gives AT the same dual-coded sense visually.
        aria-label={ariaLabel}
        className="pointer-events-auto flex items-center gap-3 rounded-2xl border border-amber-300/40 bg-amber-400/15 px-4 py-2.5 text-amber-100 backdrop-blur-md shadow-lg"
      >
        <span aria-hidden="true" className="text-2xl">{icon}</span>
        <div className="leading-tight">
          <div className="text-sm font-bold">{title}</div>
          <div className="text-xs text-amber-100/80">{blurb}</div>
        </div>
      </motion.div>
    </>
  );
}

/**
 * Gentle connection-health indicator (M3.5b "Reconnection grace"). A blip / server restart re-claims
 * the SAME seat transparently within the server's grace window, so this is deliberately calm and
 * NON-BLOCKING — never a scary modal over the world:
 *   • `connected`    — renders nothing (the normal state is invisible), except a brief, reassuring
 *     "Reconnected" pill for ~2.5s right after a successful re-claim, then it fades.
 *   • `reconnecting` — a soft "Reconnecting…" pill with an animated (motion-respecting) dot.
 *   • `lost`         — a calm "Connection lost" card with a single, friendly Reload affordance.
 *
 * Accessibility: dual-coded (icon + TEXT, never colour-alone), wrapped in `role="status"`
 * `aria-live="polite"` so assistive tech hears the state change without interrupting; the spinner dot
 * is decorative (aria-hidden) and is flattened to a static dot under reduced motion.
 */
function ConnectionIndicator({ status }: { status: ConnectionStatus }): JSX.Element | null {
  const reduceMotion = useReducedMotion();
  // Show a short "Reconnected" confirmation only after we were actually reconnecting (not on first
  // connect). We track the previous status and a transient flag that clears itself after a beat.
  const prevStatus = useRef<ConnectionStatus>(status);
  const [showReconnected, setShowReconnected] = useState(false);
  useEffect(() => {
    if (status === 'connected' && prevStatus.current === 'reconnecting') {
      setShowReconnected(true);
      const t = window.setTimeout(() => setShowReconnected(false), 2500);
      prevStatus.current = status;
      return () => window.clearTimeout(t);
    }
    prevStatus.current = status;
    return undefined;
  }, [status]);

  // The live region is ALWAYS present (even when visually empty) so a status change is announced
  // politely. role="status" + aria-live="polite" — never assertive (this must not feel like an alarm).
  const liveText =
    status === 'reconnecting'
      ? 'Reconnecting to the office'
      : status === 'lost'
        ? 'Connection lost. Reload to rejoin the office.'
        : showReconnected
          ? 'Reconnected to the office'
          : '';

  return (
    <div className="pointer-events-none flex flex-col items-center">
      {/* The sr-only span is the SOLE live region — it carries every state to screen readers. The
          visible pills below stay OUT of any live region so their text isn't double-announced. */}
      <span role="status" aria-live="polite" className="sr-only">
        {liveText}
      </span>

      {status === 'reconnecting' && (
        <div className="pointer-events-auto flex items-center gap-2 rounded-xl border border-amber-300/40 bg-amber-400/15 px-4 py-2 text-sm font-medium text-amber-100 backdrop-blur-md shadow-lg">
          <span
            aria-hidden="true"
            className={`h-2 w-2 rounded-full bg-amber-300 ${reduceMotion ? '' : 'animate-ping'}`}
          />
          <span aria-hidden="true">🔄</span>
          <span>Reconnecting…</span>
        </div>
      )}

      {status === 'lost' && (
        <div className="pointer-events-auto flex items-center gap-3 rounded-xl border border-white/15 bg-black/60 px-4 py-2 text-sm font-medium text-white/90 backdrop-blur-md shadow-lg">
          <span aria-hidden="true">🌙</span>
          <span>Connection lost.</span>
          <button
            onClick={() => window.location.reload()}
            className="rounded-lg border border-amber-300/40 bg-amber-400/20 px-3 py-1 text-xs font-semibold text-amber-100 transition hover:bg-amber-400/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/50"
          >
            Reload
          </button>
        </div>
      )}

      {status === 'connected' && showReconnected && (
        <div className="pointer-events-none flex items-center gap-2 rounded-xl border border-emerald-300/40 bg-emerald-400/15 px-4 py-2 text-sm font-medium text-emerald-100 backdrop-blur-md shadow-lg">
          <span aria-hidden="true">✅</span>
          <span>Reconnected</span>
        </div>
      )}
    </div>
  );
}

/**
 * Accessible mic input-level meter (M3.3). A live bar driven by the local RMS level, exposed as a
 * progressbar so screen-reader / keyboard users get the same "yes, your mic hears you" reassurance.
 */
function LevelMeter(): JSX.Element {
  // Subscribe to the isolated mic-level store (perf-audit R1): the ~8 Hz VAD updates re-render ONLY
  // this 1-line bar, never the whole HUD. The value stays in React, so the aria-valuenow below remains
  // live + accurate for screen readers without any separate throttle.
  const level = useSyncExternalStore(subscribeMicLevel, getMicLevel);
  const pct = Math.round(Math.max(0, Math.min(1, level)) * 100);
  return (
    <div
      role="progressbar"
      aria-label="Microphone level"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={pct}
      title="Your live microphone level (local only — never transmitted)"
      className="h-2 w-24 overflow-hidden rounded-full bg-white/10"
    >
      <div
        className="h-full rounded-full bg-emerald-400 transition-[width] duration-100"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

/**
 * One labelled audio-device dropdown (microphone OR speaker) — the input + output pickers share this
 * so styling, the always-present "System default" option, and the valid-value guard live in one place.
 * The value is always a deviceId present in `devices`, or "" for default, so React never warns about a
 * value missing from its options (labels/ids may not be known until after a permission grant).
 */
function DeviceSelect({
  icon,
  iconTitle,
  ariaLabel,
  devices,
  currentId,
  onSelect,
}: {
  icon: string;
  iconTitle: string;
  ariaLabel: string;
  devices: MicDevice[];
  currentId: string | null;
  onSelect: (deviceId: string) => void;
}): JSX.Element {
  return (
    <label className="flex items-center gap-2">
      <span aria-hidden="true" className="text-base" title={iconTitle}>{icon}</span>
      <select
        aria-label={ariaLabel}
        value={devices.some((d) => d.deviceId === currentId) ? currentId! : ''}
        onChange={(e) => onSelect(e.target.value)}
        className="max-w-[16rem] flex-1 rounded-xl border border-white/10 bg-black/60 px-2 py-1.5 text-xs text-white/90 focus:outline-none focus:ring-2 focus:ring-amber-300/50"
      >
        <option value="" className="bg-slate-900">
          System default
        </option>
        {devices.map((d) => (
          <option key={d.deviceId} value={d.deviceId} className="bg-slate-900">
            {d.label}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * Voice accessibility + push-to-talk controls (M3.3), shown only when voice is live. PTT toggle
 * (default off / open-mic), microphone + speaker device pickers, and the live level meter — all keyboard
 * operable with ARIA names.
 */
function VoicePanel({
  pttEnabled,
  micDevices,
  currentDeviceId,
  onTogglePtt,
  onSelectMic,
  speakerDevices,
  currentSinkId,
  onSelectSpeaker,
  devicesOpen,
}: {
  pttEnabled: boolean;
  micDevices: MicDevice[];
  currentDeviceId: string | null;
  onTogglePtt: () => void;
  onSelectMic: (deviceId: string) => void;
  speakerDevices: MicDevice[];
  currentSinkId: string | null;
  onSelectSpeaker: (deviceId: string) => void;
  /** Whether the device pickers are expanded (toggled by the chevron on the mic button). */
  devicesOpen: boolean;
}): JSX.Element {
  return (
    <div
      role="group"
      aria-label="Voice"
      className="pointer-events-auto mt-2 flex flex-col gap-2 rounded-2xl border border-white/10 bg-black/40 p-3 backdrop-blur-md"
    >
      <div className="flex items-center gap-2">
        <button
          onClick={onTogglePtt}
          role="switch"
          aria-checked={pttEnabled}
          aria-label="Push-to-talk"
          title={
            pttEnabled
              ? 'Push-to-talk is on — hold V to talk. Click to go back to open-mic.'
              : 'Open-mic (nearby ducks hear you). Click to switch to push-to-talk (hold V to talk).'
          }
          className={`rounded-xl border px-3 py-1.5 text-sm font-medium transition ${
            pttEnabled
              ? 'border-amber-300/40 bg-amber-400/20 text-amber-100 hover:bg-amber-400/30'
              : 'border-white/10 bg-white/5 text-white/80 hover:bg-white/10'
          }`}
        >
          {pttEnabled ? '🎙️ Push-to-talk: ON' : '🎙️ Push-to-talk: OFF'}
        </button>
        {pttEnabled && (
          <span className="text-xs text-amber-200/90">Hold <kbd>V</kbd> to talk</span>
        )}
      </div>

      <div className="flex items-center gap-2">
        <span aria-hidden="true" className="text-base">🎚️</span>
        <LevelMeter />
      </div>

      {devicesOpen && micDevices.length > 0 && (
        <DeviceSelect
          icon="🎤"
          iconTitle="Microphone (input)"
          ariaLabel="Microphone"
          devices={micDevices}
          currentId={currentDeviceId}
          onSelect={onSelectMic}
        />
      )}

      {devicesOpen && speakerDevices.length > 0 && (
        <DeviceSelect
          icon="🎧"
          iconTitle="Speaker / headphones (output)"
          ariaLabel="Speaker (audio output)"
          devices={speakerDevices}
          currentId={currentSinkId}
          onSelect={onSelectSpeaker}
        />
      )}
    </div>
  );
}

/**
 * A single Discord-style audio-device indicator: a compact glyph (🎤 / 🎧) that reads "on" at full
 * strength and "off" dimmed with a red slash struck through it. DUAL-CODED for a11y — the meaning never
 * lives in colour alone: the glyph + a precise `aria-label`/`title` carry it, and the slash is a SHAPE
 * (not just a hue) so it survives colour-blindness and high-contrast modes.
 */
function DeviceIcon({ icon, on, label }: { icon: string; on: boolean; label: string }): JSX.Element {
  return (
    <span
      role="img"
      aria-label={label}
      title={label}
      className={`relative inline-flex h-4 w-4 items-center justify-center text-[12px] leading-none ${
        on ? '' : 'opacity-40'
      }`}
    >
      <span aria-hidden="true">{icon}</span>
      {!on && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 flex items-center justify-center"
        >
          <span className="block h-[1.5px] w-[130%] -rotate-45 rounded-full bg-rose-400/90" />
        </span>
      )}
    </span>
  );
}

/**
 * One participant row in the session roster: their critter glyph + name (tinted with their character
 * colour), a "(you)" tag for the local player, the two Discord-style device indicators (🎤 mic, 🎧 can-
 * hear), and — only for citizens I can actually HEAR — a quiet voice-Block control (self-protection,
 * never punishment; the blocked party is never told or shown).
 */
// memo (perf-audit R2): with a referentially-stable `member` (roomSections only changes on a real
// roster poll) and a stable `onBlock` (useCallback below), a HUD re-render that didn't touch the roster
// — e.g. a chat line arriving while the People panel is open — skips reconciling all N rows.
const RosterMemberRow = memo(function RosterMemberRow({
  member,
  audible,
  onBlock,
}: {
  member: RosterMember;
  audible: boolean;
  onBlock: (sessionId: string) => void;
}): JSX.Element {
  if (import.meta.env.DEV) bumpRender('rosterRow'); // perf-audit M4 (R2): memo-skipped rows won't bump
  const emoji = SPECIES_META[member.species as DuckSpecies]?.emoji ?? '🦆';
  const micOn = member.voiceOn && !member.micMuted;
  // The three honest states (no "deafen" feature ⇒ no mic-on-while-not-hearing): in voice + speaking,
  // in voice + muted, or not in voice at all.
  const micLabel = !member.voiceOn
    ? 'Not in voice chat'
    : member.micMuted
      ? 'Microphone muted'
      : 'Microphone on';
  const hearLabel = member.voiceOn
    ? 'Can hear voice chat'
    : 'Not hearing voice chat (no audio output)';
  // Voice controls (personal volume + block) ride only on rows I can actually HEAR, never on my own.
  const canTrim = audible && !member.isSelf;
  return (
    <li className="flex flex-col gap-1.5 rounded-xl bg-white/5 px-2.5 py-1.5">
      <div className="flex items-center justify-between gap-2">
        <span className="flex min-w-0 items-center gap-1.5 text-sm">
          <span aria-hidden="true">{emoji}</span>
          <span className="truncate font-medium" style={{ color: nameColorCss(member.duckColor) }}>
            {member.name}
          </span>
          {member.isSelf && <span className="shrink-0 text-[11px] text-white/40">(you)</span>}
        </span>
        <span className="flex shrink-0 items-center gap-2">
          <span className="flex items-center gap-1">
            <DeviceIcon icon="🎤" on={micOn} label={micLabel} />
            <DeviceIcon icon="🎧" on={member.voiceOn} label={hearLabel} />
          </span>
          {canTrim && (
            <button
              onClick={() => onBlock(member.id)}
              aria-label={`Block voice from ${member.name}`}
              title={`Stop hearing ${member.name} — just for you. They’re never told, and you can undo it anytime.`}
              className="flex shrink-0 items-center gap-1 rounded-lg border border-white/10 bg-white/5 px-2 py-1 text-xs font-medium text-white/80 transition hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/50"
            >
              <span aria-hidden="true">🔇</span>
              <span>Block</span>
            </button>
          )}
        </span>
      </div>
      {/* Discord-style PERSONAL volume for this duck — just for me, never told to them. Only on rows I hear. */}
      {canTrim && <PeerVolumeSlider userId={member.userId} name={member.name} />}
    </li>
  );
});

/**
 * A per-participant PERSONAL voice-volume knob (Discord-style) — how loudly *I* hear this one duck, layered
 * over the flat per-peer zone gain. Runs from −100 (silenced) through 0 (normal, the default centre detent)
 * to +100 (doubled). LOCAL & per-device: the trimmed duck is never told, nothing is scored (Constitution
 * Art. 1 — a comfort knob, not surveillance). Reads/writes the persisted {@link usePeerVolumeStore} directly
 * (keyed by the peer's stable userId), so the value survives their reconnect and a page refresh; the
 * audio-settings apply hook pushes each change to the live VoiceClient. Dual-coded (words, not colour) and
 * fully keyboard-operable; double-click resets to normal.
 */
function PeerVolumeSlider({ userId, name }: { userId: string; name: string }): JSX.Element {
  const offset = usePeerVolumeStore((s) => s.volumes[userId] ?? 0);
  const setPeerVolume = usePeerVolumeStore((s) => s.setPeerVolume);
  const resetPeerVolume = usePeerVolumeStore((s) => s.resetPeerVolume);
  const readout = offset === 0 ? 'Normal' : offset > 0 ? `+${offset}` : `${offset}`;
  const valueText =
    offset === 0
      ? 'Normal volume'
      : offset > 0
        ? `${offset} percent louder`
        : `${-offset} percent quieter`;
  return (
    <div className="flex items-center gap-2 pl-6">
      <span aria-hidden="true" className="text-xs" title="Personal volume">
        🔊
      </span>
      <input
        type="range"
        min={PERSONAL_VOICE_MIN}
        max={PERSONAL_VOICE_MAX}
        step={5}
        value={offset}
        aria-label={`How loudly you hear ${name} (double-click, or Delete, to reset to normal)`}
        aria-valuetext={valueText}
        title={`How loudly you hear ${name} — just for you. Double-click (or press Delete) to reset to normal.`}
        onChange={(e) => setPeerVolume(userId, Number(e.target.value))}
        onDoubleClick={() => resetPeerVolume(userId)}
        // Keyboard parity for the mouse double-click reset (a11y): Delete / Backspace / 0 snap back to
        // normal. Arrow keys keep the browser's native range stepping (we only claim the reset keys).
        onKeyDown={(e) => {
          if (e.key === 'Delete' || e.key === 'Backspace' || e.key === '0') {
            e.preventDefault();
            resetPeerVolume(userId);
          }
        }}
        className="h-1.5 min-w-0 flex-1 cursor-pointer accent-amber-400"
      />
      {/* Words-not-colour readout of the trim, so the state never rests on the slider position alone.
          aria-hidden — the slider's own aria-valuetext is the SR source of truth. */}
      <span
        aria-hidden="true"
        className="w-9 shrink-0 text-right text-[10px] tabular-nums text-white/40"
      >
        {readout}
      </span>
    </div>
  );
}

/**
 * Session-roster panel — the "who's in the office, and where" list (Community Manager: belonging, never
 * surveillance — it helps ducks FIND each other, never ranks/times/shames anyone). Shows EVERYONE in the
 * session grouped into room sections; the viewer's OWN room is accented + pinned first, busy rooms surface
 * next, and the catch-all "Open floor" sinks last. Each row carries Discord-style 🎤/🎧 device indicators
 * (who can speak, who can hear) that update live. The voice-Block affordance (M3.4) rides inline on the
 * rows I can hear; a separate "Blocked" section lets me undo. Friendly empty state; fully keyboard-
 * operable; every control DUAL-CODED (icon + text), never colour-alone (a11y).
 */
function PeoplePanel({
  roomSections,
  audibleSessionIds,
  blockedPeers,
  onBlockCitizen,
  onUnblockCitizen,
  embedded = false,
}: {
  roomSections: RosterSection[];
  audibleSessionIds: Set<string>;
  blockedPeers: BlockedPeer[];
  onBlockCitizen: (sessionId: string) => void;
  onUnblockCitizen: (sessionId: string) => void;
  /**
   * When hosted inside a HUD menu popover, the parent `HudMenu` already owns focus-on-open + restore-to-
   * toggle, so we DON'T re-run our own (which would fight it). We still keep the FIX-3 refocus-after-
   * block/unblock (focus must not fall to <body> when a row unmounts) and drop the outer card chrome so
   * the panel sits flush in the popover.
   */
  embedded?: boolean;
}): JSX.Element {
  const panelRef = useRef<HTMLDivElement>(null);

  // FIX 5 — focus-on-open + restore-on-close (WCAG 2.4.3 focus order), mirroring GuildPanel's `Panel`.
  // The People TOGGLE that opened us sits OUTSIDE this panel, so on open we remember it and pull focus
  // into the panel (the keyboard/SR user lands in context); on close we hand focus back to the toggle.
  // Deliberately NOT a focus-trap — this is a non-blocking slide-over beside a still-interactive world.
  // Skipped when `embedded`: the host HudMenu already manages focus-on-open + restore-to-toggle.
  useEffect(() => {
    if (embedded) return undefined;
    const opener = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();
    return () => {
      if (opener && opener.isConnected && typeof opener.focus === 'function') opener.focus();
    };
  }, [embedded]);

  // FIX 3 — don't drop focus when the activated row unmounts (WCAG 2.4.3). Blocking can drop the peer's
  // row from the audible set (the Block control disappears), so focus would otherwise fall to <body>.
  // After a block/unblock we move focus to the stable panel container so the keyboard user stays in
  // context. We focus on the next frame so the row has settled first.
  // Stable refs (perf-audit R2) so memo(RosterMemberRow) isn't invalidated by a fresh onBlock each
  // render. panelRef is a ref (stable); onBlockCitizen/onUnblockCitizen are already useCallback'd upstream.
  const refocusPanel = useCallback((): void => {
    requestAnimationFrame(() => panelRef.current?.focus());
  }, []);
  const handleBlock = useCallback(
    (sessionId: string): void => {
      onBlockCitizen(sessionId);
      refocusPanel();
    },
    [onBlockCitizen, refocusPanel],
  );
  const handleUnblock = useCallback(
    (sessionId: string): void => {
      onUnblockCitizen(sessionId);
      refocusPanel();
    },
    [onUnblockCitizen, refocusPanel],
  );

  const totalPeople = roomSections.reduce((n, s) => n + s.occupants.length, 0);
  const ducks = (n: number): string => `${n} ${n === 1 ? 'duck' : 'ducks'}`;

  return (
    <div
      ref={panelRef}
      role="group"
      aria-label="People in the office"
      // tabIndex={-1} makes the panel programmatically focusable (FIX 5 landing spot + FIX 3 refocus
      // target) without adding it to the Tab order; outline-none keeps the focus ring off the container
      // while the inner controls keep their own visible focus-visible rings.
      tabIndex={-1}
      className={
        embedded
          ? 'pointer-events-auto flex flex-col gap-3 outline-none'
          : 'pointer-events-auto mt-2 flex w-[min(18rem,calc(100vw-1.5rem))] flex-col gap-3 rounded-2xl border border-white/10 bg-black/50 p-3 outline-none backdrop-blur-md'
      }
    >
      {/* Everyone in the session, grouped by room */}
      <div className="flex flex-col gap-2">
        <h2 className="text-[10px] font-semibold uppercase tracking-wide text-white/40">
          👥 In the office · {ducks(totalPeople)}
        </h2>
        {totalPeople <= 1 ? (
          <p className="text-xs text-white/50">
            You’re the only duck here right now. Invite a few friends to bring the office to life. 🦆
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            {roomSections.map((section) => (
              <section
                key={section.zoneId ?? '__open-floor'}
                aria-label={`${section.zoneName}${section.isCurrent ? ' (your room)' : ''}, ${ducks(
                  section.occupants.length,
                )}`}
                className={
                  section.isCurrent
                    ? 'flex flex-col gap-1 rounded-xl bg-amber-300/5 p-1.5 ring-1 ring-amber-300/30'
                    : 'flex flex-col gap-1'
                }
              >
                <h3 className="flex items-center gap-1.5 px-1 text-[11px] font-semibold uppercase tracking-wide text-white/55">
                  {section.isCurrent && <span aria-hidden="true">📍</span>}
                  <span className="truncate">{section.zoneName}</span>
                  <span className="font-normal text-white/35">· {section.occupants.length}</span>
                  {section.isCurrent && (
                    <span className="ml-auto shrink-0 rounded-full bg-amber-300/15 px-1.5 py-px text-[10px] font-medium normal-case tracking-normal text-amber-200/90">
                      you’re here
                    </span>
                  )}
                </h3>
                <ul className="flex flex-col gap-1">
                  {section.occupants.map((m) => (
                    <RosterMemberRow
                      key={m.id}
                      member={m}
                      audible={audibleSessionIds.has(m.id)}
                      onBlock={handleBlock}
                    />
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}
      </div>

      {/* People I've blocked — only shown when there are any (keeps the panel calm + non-shaming) */}
      {blockedPeers.length > 0 && (
        <div className="flex flex-col gap-1.5 border-t border-white/10 pt-2.5">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-white/40">
            🚫 Blocked · only you can see this
          </div>
          <ul className="flex flex-col gap-1">
            {blockedPeers.map((peer) => (
              <li
                key={peer.userId}
                className="flex flex-col gap-1 rounded-xl bg-white/5 px-2.5 py-1.5"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="flex min-w-0 items-center gap-1.5 text-sm text-white/70">
                    <span aria-hidden="true">🔇</span>
                    <span className="truncate">{peer.name}</span>
                  </span>
                  <button
                    onClick={() => peer.sessionId && handleUnblock(peer.sessionId)}
                    disabled={!peer.online || !peer.sessionId}
                    aria-label={
                      peer.online
                        ? `Unblock ${peer.name}`
                        : `Unblock ${peer.name} (unavailable while they’re away)`
                    }
                    title={
                      peer.online
                        ? `Hear ${peer.name} again`
                        : `${peer.name} is away — you can lift this when they’re back`
                    }
                    className="flex shrink-0 items-center gap-1 rounded-lg border border-emerald-300/30 bg-emerald-400/10 px-2 py-1 text-xs font-medium text-emerald-100 transition hover:bg-emerald-400/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-emerald-400/10"
                  >
                    <span aria-hidden="true">🔊</span>
                    <span>Unblock</span>
                  </button>
                </div>
                {/* Offline hint — dual-coded text (never colour-alone) so the disabled control is explained. */}
                {!peer.online && (
                  <span className="text-[11px] leading-snug text-white/40">
                    They’re away — you can lift this when they’re back.
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * ELECTED presence picker (M4). Self-expression, NOT a status others impose — the citizen chooses a
 * flair to display (or "None" to clear it). Keyboard-reachable buttons, each DUAL-CODED with the
 * flavor's icon + text label (never colour-only); the current election is shown via aria-pressed and
 * a highlighted button. Anti-surveillance: nothing here is inferred from behaviour.
 */
function PresencePicker({
  myPresence,
  onSetPresence,
  embedded = false,
}: {
  myPresence: PresenceFlavor;
  onSetPresence: (flavor: PresenceFlavor) => void;
  /** When inside a HUD menu popover, drop the outer card chrome so it sits flush (no box-in-box). */
  embedded?: boolean;
}): JSX.Element {
  const electable = PRESENCE_FLAVORS.filter((f): f is Exclude<PresenceFlavor, 'none'> => f !== 'none');
  return (
    <div
      role="group"
      aria-label="Presence"
      // Keyboard-reachable: the group itself takes focus (Tab) so a keyboard/SR user lands on the
      // control, then tabs through its option buttons — satisfies the M4 accessibility gate.
      tabIndex={0}
      className={
        embedded
          ? 'pointer-events-auto flex flex-col gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/50'
          : 'pointer-events-auto flex flex-col gap-2 rounded-2xl border border-white/10 bg-black/40 p-3 backdrop-blur-md focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/50'
      }
    >
      {!embedded && (
        <div className="text-[10px] font-semibold uppercase tracking-wide text-white/40">
          Presence · your choice
        </div>
      )}
      <div className="flex flex-wrap gap-1.5">
        {electable.map((flavor) => {
          const { icon, label } = PRESENCE_LABELS[flavor];
          const active = myPresence === flavor;
          return (
            <button
              key={flavor}
              onClick={() => onSetPresence(active ? 'none' : flavor)}
              aria-pressed={active}
              title={`Show "${label}" over your duck — a flair you choose, never tracked`}
              className={`flex items-center gap-1 rounded-xl border px-2.5 py-1.5 text-xs font-medium transition ${
                active
                  ? 'border-amber-300/50 bg-amber-400/25 text-amber-100'
                  : 'border-white/10 bg-white/5 text-white/80 hover:bg-white/10'
              }`}
            >
              <span aria-hidden="true">{icon}</span>
              <span>{label}</span>
            </button>
          );
        })}
        <button
          onClick={() => onSetPresence('none')}
          aria-pressed={myPresence === 'none'}
          aria-label="Clear presence flair"
          title="Clear your presence flair (show nothing over your duck)"
          className={`flex items-center gap-1 rounded-xl border px-2.5 py-1.5 text-xs font-medium transition ${
            myPresence === 'none'
              ? 'border-white/30 bg-white/15 text-white'
              : 'border-white/10 bg-white/5 text-white/80 hover:bg-white/10'
          }`}
        >
          Clear
        </button>
      </div>
    </div>
  );
}

/**
 * Interact prompt/affordance (M4). When a world object is in range it shows a dual-coded prompt
 * (the object's icon + label) and the "E" key hint, AND is itself a clickable/keyboard button — so
 * interaction never depends solely on the in-world keypress (accessibility gate). Using an object is
 * a DELIBERATE action that elects a presence, never inferred surveillance.
 */
function InteractPrompt({
  object,
  onInteract,
}: {
  object: InteractiveObject;
  onInteract: () => void;
}): JSX.Element {
  return (
    <button
      onClick={onInteract}
      aria-label={`Use ${object.label} — press E`}
      title={`Use ${object.label} (press E)`}
      className="pointer-events-auto flex items-center gap-2 rounded-2xl border border-amber-300/40 bg-amber-400/15 px-4 py-2 text-sm font-medium text-amber-100 backdrop-blur-md transition hover:bg-amber-400/25"
    >
      <span aria-hidden="true" className="text-lg">{object.icon}</span>
      <span>
        Press <kbd>E</kbd> — {object.label}
      </span>
    </button>
  );
}

/**
 * Sit / Stand prompt (Task 2 "interactive SIT"). When seated it offers "Stand up"; when standing next to
 * an empty seat it offers "Sit". Like the InteractPrompt it's dual-coded (icon + label) and itself a
 * button, so sitting never depends solely on the in-world E keypress (a11y). Renders nothing when there's
 * nothing to do (standing + no seat in range). E toggles the same action.
 */
function SeatPrompt({
  nearestSeat,
  seated,
  onSit,
}: {
  nearestSeat: Seat | null;
  /** Whether the local duck is currently seated (drives "Stand up" vs "Sit"). */
  seated: boolean;
  onSit: () => void;
}): JSX.Element | null {
  if (!seated && !nearestSeat) return null;
  const label = seated ? 'Stand up' : `Sit — ${nearestSeat!.label}`;
  const icon = seated ? '🧍' : (nearestSeat!.icon ?? '💺');
  return (
    <button
      onClick={onSit}
      aria-label={seated ? 'Stand up — press E' : `Sit on ${nearestSeat!.label} — press E`}
      title={seated ? 'Stand up (press E)' : `Sit down (press E)`}
      className="pointer-events-auto flex items-center gap-2 rounded-2xl border border-amber-300/40 bg-amber-400/15 px-4 py-2 text-sm font-medium text-amber-100 backdrop-blur-md transition hover:bg-amber-400/25"
    >
      <span aria-hidden="true" className="text-lg">{icon}</span>
      <span>
        Press <kbd>E</kbd> — {label}
      </span>
    </button>
  );
}

/**
 * WORK prompt (gamified-XP redesign). Shown only while the local duck is SEATED AT A DESK (a workstation
 * seat). A repeatable "Work" button that sends a desk-work beat — the server re-checks seated-at-a-desk +
 * the cooldown and attests the tiny +1 XP, which lands back as a warm "✨ +1 XP" toast. It's the gentle
 * "I'm heads-down at my desk" beat, never a grind: spamming it does nothing inside the server cooldown.
 * Dual-coded (icon + label) and itself a button, so it never depends on the in-world keypress (a11y).
 */
function WorkPrompt({ onWork }: { onWork: () => void }): JSX.Element {
  return (
    <button
      onClick={onWork}
      aria-label="Do a beat of work at your desk — earns a little XP"
      title="Heads-down work — a small, repeatable beat at your desk that earns a little XP 💻"
      className="pointer-events-auto flex items-center gap-2 rounded-2xl border border-emerald-300/40 bg-emerald-400/15 px-4 py-2 text-sm font-medium text-emerald-100 backdrop-blur-md transition hover:bg-emerald-400/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/50"
    >
      <span aria-hidden="true" className="text-lg">💻</span>
      <span>Work</span>
    </button>
  );
}

/**
 * Entryway front-door prompt (Floorplan v2 / ADR-0022). Shown when a front-door action is in range — ring
 * the bell, knock, or open the door — so a newcomer in the vestibule (and the greeter inside) always has a
 * clear, dual-coded affordance. Like the other prompts it's itself a button, so the action never depends on
 * the in-world E keypress (a11y). A warmer sky-blue tint sets it apart from the amber interact prompts.
 */
function EntryActionPrompt({
  action,
  onAction,
}: {
  action: EntryActionView;
  onAction: () => void;
}): JSX.Element {
  return (
    <button
      onClick={onAction}
      aria-label={`${action.label} — press E`}
      title={`${action.label} (press E)`}
      className="pointer-events-auto flex items-center gap-2 rounded-2xl border border-sky-300/40 bg-sky-400/15 px-4 py-2 text-sm font-medium text-sky-100 backdrop-blur-md transition hover:bg-sky-400/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-300/50"
    >
      <span aria-hidden="true" className="text-lg">{action.icon}</span>
      <span>
        Press <kbd>E</kbd> — {action.label}
      </span>
    </button>
  );
}

/**
 * Host-only "Doors" control (Room Doors). The HOST (workspace OWNER) may open/close ANY door from anywhere —
 * the in-world "[E] Open/Close" prompt only reaches doors you're standing next to, so this control is the
 * host's reach to every door (incl. off-screen ones) + the entry vestibule door. A collapsible list of the
 * controllable doorways with a single Open/Close toggle each, reflecting live state (so doors others toggle
 * update here too). Keyboard-accessible buttons throughout. The SERVER re-checks host on every toggle.
 *
 * It lives INSIDE the bottom-centre action bar (right of Challenge): a relative anchor whose list pops up
 * ABOVE the trigger so it never collides with the row.
 */
function HostDoorsPanel({
  menus,
  doorState,
  doorways,
  onToggle,
}: {
  /** Shared action-bar menu controller — Doors joins it so only ONE popover in the row is ever open. */
  menus: InteractionMenus;
  doorState: { closed: string[]; entryOpen: boolean; held: string[] };
  /** The RESOLVED level's interior doorways (Phase 3) — listed in the panel below the entry door. */
  doorways: readonly Doorway[];
  onToggle: (doorwayId: string, open: boolean) => void;
}): JSX.Element {
  // Doors is a sibling of the Reactions/Sounds/Animations/Hold popovers, so it shares their controller:
  // opening it closes any other menu (one-open-at-a-time), and Esc closes it — consistent with the row.
  const open = menus.open === MENU.doors;
  const closed = new Set(doorState.closed);
  const held = new Set(doorState.held);
  // The entry vestibule door first (its own ritual), then every interior doorway in floor-plan order.
  const rows: { id: string; name: string; isOpen: boolean; isHeld: boolean }[] = [
    { id: ENTRY_DOORWAY_ID, name: 'Entryway (front door)', isOpen: doorState.entryOpen, isHeld: false },
    ...doorways.map((dw) => ({
      id: dw.id,
      name: dw.name,
      isOpen: !closed.has(dw.id),
      isHeld: held.has(dw.id),
    })),
  ];
  return (
    <div className="relative flex flex-col items-end">
      {open && (
        <div
          role="group"
          aria-label="Doors — open or close any door (host)"
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.stopPropagation();
              menus.closeMenu();
            }
          }}
          className="absolute bottom-full right-0 mb-2 max-h-[60vh] w-64 overflow-y-auto rounded-2xl border border-amber-300/30 bg-black/80 p-3 text-amber-50 shadow-xl backdrop-blur-md"
        >
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-amber-200/70">
            🚪 Doors (host)
          </div>
          <ul className="flex flex-col gap-1">
            {rows.map((r) => (
              <li key={r.id} className="flex items-center justify-between gap-2 text-sm">
                <span className="truncate" title={r.name}>
                  {r.name}
                  {r.isHeld && (
                    <span
                      aria-label="Locked by a player — as host you can still open it"
                      title="Locked by a player — as host you can still open it"
                      className="ml-1"
                    >
                      🔒
                    </span>
                  )}
                </span>
                <button
                  onClick={() => onToggle(r.id, !r.isOpen)}
                  aria-label={`${r.isOpen ? 'Close' : 'Open'} ${r.name}`}
                  className={`shrink-0 rounded-lg border px-2 py-0.5 text-xs font-semibold transition focus-visible:outline-none focus-visible:ring-2 ${
                    r.isOpen
                      ? 'border-emerald-300/40 bg-emerald-400/15 text-emerald-100 hover:bg-emerald-400/25 focus-visible:ring-emerald-300/50'
                      : 'border-rose-300/40 bg-rose-400/15 text-rose-100 hover:bg-rose-400/25 focus-visible:ring-rose-300/50'
                  }`}
                >
                  {r.isOpen ? 'Close' : 'Open'}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      <button
        type="button"
        onClick={() => menus.toggleMenu(MENU.doors)}
        aria-expanded={open}
        aria-label="Doors — open or close any door"
        title="Open or close any door (host)"
        className="flex h-10 items-center gap-1.5 rounded-xl border border-amber-300/40 bg-amber-700/25 px-3 text-sm font-medium text-amber-100 transition hover:bg-amber-700/35 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/60"
      >
        <span aria-hidden="true">🚪</span>
        <span className="hidden sm:inline">Doors</span>
      </button>
    </div>
  );
}

/**
 * Interior room-door prompt (Room Doors). Shown when a doorway is in reach — "Open door" if it's shut,
 * "Close door" if it's open — so anyone standing at a door has a clear, dual-coded affordance (soft close:
 * anyone next to it may toggle). Like the other prompts it's itself a button, so the action never depends on
 * the in-world E keypress (a11y). A warm wood-amber tint, distinct from the sky-blue front-door prompt.
 */
function DoorActionPrompt({
  action,
  onAction,
}: {
  action: DoorActionView;
  onAction: () => void;
}): JSX.Element {
  return (
    <button
      onClick={onAction}
      aria-label={`${action.label} — press E`}
      title={`${action.label} (press E)`}
      className="pointer-events-auto flex items-center gap-2 rounded-2xl border border-amber-300/40 bg-amber-700/20 px-4 py-2 text-sm font-medium text-amber-100 backdrop-blur-md transition hover:bg-amber-700/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/50"
    >
      <span aria-hidden="true" className="text-lg">{action.icon}</span>
      <span>
        Press <kbd>E</kbd> — {action.label}
      </span>
    </button>
  );
}

/**
 * Shared meeting progress bar (gamified-XP redesign). When the room-wide meeting quest is forming or in
 * session (`phase !== 'idle'`) this shows the SAME "🪑 X/8 · Daily Standup 0:42 / 1:00" cue to every
 * client, with a progress fill, so the whole office watches the co-op moment build toward its celebration
 * (the server fires a CELEBRATE 'quest_completed' on completion → confetti). Renders nothing when idle.
 *
 * Accessibility: dual-coded (icon + TEXT, never colour-alone), a real role="progressbar" with min/max/now
 * + a full aria-label, and a polite role="status" announce when the meeting STARTS. Purely celebratory —
 * it shows the ROOM's shared progress, never any person (Constitution Art. 1: not a surveillance surface).
 */
function MeetingBar({
  meeting,
}: {
  meeting: { seated: number; phase: 'idle' | 'standup' | 'meeting'; elapsedSec: number; targetSec: number };
}): JSX.Element | null {
  const reduceMotion = useReducedMotion();
  // Announce ONLY on a START (idle → forming), never every render while it runs. Clears on end so it
  // re-fires for the next meeting rather than staying stuck on the last one.
  const prevPhase = useRef(meeting.phase);
  const [announcement, setAnnouncement] = useState('');
  const title = meeting.phase === 'meeting' ? 'Meeting in session' : 'Daily standup forming';
  useEffect(() => {
    if (prevPhase.current === meeting.phase) return;
    const wasIdle = prevPhase.current === 'idle';
    prevPhase.current = meeting.phase;
    if (meeting.phase === 'idle') {
      setAnnouncement('');
      return;
    }
    if (wasIdle) setAnnouncement(`${title} — gather in the Meeting Room.`);
  }, [meeting.phase, title]);

  const live = (
    <span role="status" aria-live="polite" className="sr-only">
      {announcement}
    </span>
  );

  if (meeting.phase === 'idle') return live;

  const elapsed = Math.max(0, Math.floor(meeting.elapsedSec));
  const target = Math.max(1, Math.floor(meeting.targetSec));
  const pct = Math.min(100, Math.round((elapsed / target) * 100));
  const fmt = (s: number): string => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  const timeText = `${fmt(Math.min(elapsed, target))} / ${fmt(target)}`;
  const ariaLabel = `${title}. ${meeting.seated} of 8 seats filled. ${timeText}.`;

  return (
    <>
      {live}
      <div
        aria-label={ariaLabel}
        className="pointer-events-auto w-[min(18rem,calc(100vw-1.5rem))] rounded-2xl border border-amber-300/40 bg-amber-400/15 px-4 py-2.5 text-amber-100 backdrop-blur-md shadow-lg"
      >
        <div className="flex items-center justify-between gap-2 text-sm font-bold">
          <span className="flex items-center gap-1.5">
            <span aria-hidden="true">🪑</span>
            {title}
          </span>
          <span aria-hidden="true" className="text-xs font-semibold text-amber-100/80">
            {meeting.seated}/8
          </span>
        </div>
        <div
          role="progressbar"
          aria-label={`${title} progress`}
          aria-valuemin={0}
          aria-valuemax={target}
          aria-valuenow={Math.min(elapsed, target)}
          className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-black/30"
        >
          <div
            className={`h-full rounded-full bg-amber-300 ${reduceMotion ? '' : 'transition-[width] duration-500'}`}
            style={{ width: `${pct}%` }}
          />
        </div>
        <div aria-hidden="true" className="mt-1 text-right text-[11px] font-medium text-amber-100/70">
          {timeText}
        </div>
      </div>
    </>
  );
}

/**
 * HOLD picker (Task 2 "HOLD ITEMS"). A disclosure (the SAME accessible pattern the soundboard/reactions
 * use): a labelled toggle revealing a grid of the closed-set `HELD_ITEMS` props + an "Empty hands"
 * option. Picking one sends SET_HELD (cosmetic-only — it changes how your duck LOOKS, never what it can
 * do). Dual-coded (emoji + text) buttons, keyboard-operable, Esc-closes, focus restored to the toggle.
 */
function HoldPicker({
  current,
  isOpen,
  anyOpen,
  onToggle,
  onClose,
  onHoldItem,
}: {
  current: string;
  /** Whether THIS menu is the open one (driven centrally so only one menu is ever open). */
  isOpen: boolean;
  /** Whether ANY menu is open right now — so closing-to-switch doesn't yank focus back to this toggle. */
  anyOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  onHoldItem: (item: string) => void;
}): JSX.Element {
  const reduceMotion = useReducedMotion();
  const toggleRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  useMenuFocus(isOpen, anyOpen, popoverRef, toggleRef);
  const pick = (item: string): void => {
    onHoldItem(item);
    onClose();
  };
  const held = heldItemById(current);

  return (
    <div className="relative">
      <button
        ref={toggleRef}
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-keyshortcuts="4"
        aria-label={held ? `Holding ${held.name} — change held item` : 'Hold an item'}
        title="Hold a fun prop in hand — a guitar, laptop, coffee… (looks only). Press 4, then an item number."
        className={`flex h-10 w-10 items-center justify-center rounded-xl border text-xl transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/50 ${
          isOpen || held
            ? 'border-amber-300/50 bg-amber-400/25'
            : 'border-white/10 bg-white/5 hover:bg-white/15'
        }`}
      >
        <span aria-hidden="true">{held ? held.emoji : '✋'}</span>
      </button>
      {isOpen && (
        <div
          ref={popoverRef}
          role="group"
          aria-label="Pick something to hold"
          tabIndex={-1}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.stopPropagation();
              onClose();
            }
          }}
          className="absolute bottom-12 left-0 grid w-[min(15rem,calc(100vw-1.5rem))] grid-cols-2 gap-1.5 rounded-2xl border border-white/10 bg-black/70 p-2 outline-none backdrop-blur-md shadow-lg"
        >
          {/* Empty hands first — always available so you can put a prop down. Not number-selectable (the
              numbers index the props themselves, 1-based, matching their order below). */}
          <button
            type="button"
            onClick={() => pick('')}
            aria-pressed={current === ''}
            aria-label="Empty hands"
            title="Put down whatever you're holding"
            className={`col-span-2 flex items-center gap-1.5 rounded-xl px-2 py-2 text-left text-sm font-medium text-white transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/50 ${
              current === '' ? 'bg-amber-400/25' : 'bg-white/5 hover:bg-white/15'
            }`}
          >
            <span aria-hidden="true" className="text-lg leading-none">✋</span>
            <span className="min-w-0 flex-1 truncate">Empty hands</span>
          </button>
          {HELD_ITEMS.map((item, i) => {
            const active = current === item.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => pick(item.id)}
                aria-pressed={active}
                aria-label={`Hold ${item.name}`}
                title={`Hold ${item.name} — press ${i + 1}`}
                className={`flex items-center gap-1.5 rounded-xl px-2 py-2 text-left text-sm font-medium text-white transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/50 ${
                  active ? 'bg-amber-400/25' : 'bg-white/5 hover:bg-white/15'
                } ${reduceMotion ? '' : 'hover:scale-[1.03]'}`}
              >
                <span aria-hidden="true" className="text-lg leading-none">{item.emoji}</span>
                <span className="min-w-0 flex-1 truncate">{item.name}</span>
                <NumBadge n={i + 1} />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * AnimationsPicker (UX declutter pass): the four emotes (wave / dance / laugh / coffee) gathered into
 * ONE disclosure — the same accessible pattern as the reactions / sounds / hold dropdowns — instead of
 * four always-on buttons, so the action bar reads as one calm block. The 1–4 number keys still fire the
 * emotes (handled in the input layer, independent of these buttons), and each option keeps its "Wave (1)"
 * title hint. Picking one plays it + closes the popover; Esc closes; focus restores to the toggle.
 */
function AnimationsPicker({
  isOpen,
  anyOpen,
  onToggle,
  onClose,
  onEmote,
}: {
  isOpen: boolean;
  anyOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  onEmote: (animation: Animation) => void;
}): JSX.Element {
  const reduceMotion = useReducedMotion();
  const toggleRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  useMenuFocus(isOpen, anyOpen, popoverRef, toggleRef);
  const pick = (animation: Animation): void => {
    onEmote(animation);
    onClose();
  };
  return (
    <div className="relative">
      <button
        ref={toggleRef}
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-keyshortcuts="3"
        aria-label="Animations"
        title="Play an animation — wave, dance, laugh… Press 3, then an animation number."
        className={`flex h-10 w-10 items-center justify-center rounded-xl border text-xl transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/50 ${
          isOpen ? 'border-amber-300/50 bg-amber-400/25' : 'border-white/10 bg-white/5 hover:bg-white/15'
        }`}
      >
        <span aria-hidden="true">🎭</span>
      </button>
      {isOpen && (
        <div
          ref={popoverRef}
          role="group"
          aria-label="Pick an animation"
          tabIndex={-1}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.stopPropagation();
              onClose();
            }
          }}
          className="absolute bottom-12 left-0 grid w-[min(13rem,calc(100vw-1.5rem))] grid-cols-2 gap-1.5 rounded-2xl border border-white/10 bg-black/70 p-2 outline-none backdrop-blur-md shadow-lg"
        >
          {EMOTES.map((e, i) => (
            <button
              key={e.key}
              type="button"
              onClick={() => pick(e.key)}
              aria-label={e.title}
              title={`${e.title} — press ${i + 1}`}
              className={`flex items-center gap-1.5 rounded-xl bg-white/5 px-2 py-2 text-left text-sm font-medium text-white transition hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/50 ${
                reduceMotion ? '' : 'hover:scale-[1.03]'
              }`}
            >
              <span aria-hidden="true" className="text-lg leading-none">{e.icon}</span>
              <span className="min-w-0 flex-1 truncate">{e.title}</span>
              <NumBadge n={i + 1} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * The unified bottom-centre ACTION BAR (UX declutter pass). One calm block gathers the four "express
 * yourself" menus — 😀 Reactions · 🔊 Sounds · 🎭 Animations · ✋ Hold — plus the 🪨📄✂️ Challenge tap.
 *
 * All four menus are driven by ONE central controller ({@link useInteractionMenus}, owned by the HUD):
 * only one is ever open, each opens on a number HOTKEY (1–4), and while open the digit keys pick an item
 * by its number (type-a-number, debounced). Each keeps its original accessible name + the focus-on-open /
 * Esc-close / restore-to-toggle pattern. The speech INPUT lives in the resizable ChatPanel now, so this
 * bar is purely quick actions.
 */
function ActionBar({
  menus,
  onReact,
  onSoundReact,
  onSoundPrefetch,
  onEmote,
  myHeldItem,
  onHoldItem,
  onChallengeNearest,
  challengeDisabled,
  challengeRef,
  isHost,
  doorState,
  doorways,
  onHostToggleDoor,
}: {
  /** The shared interaction-menu controller (open-state + type-a-number selection). */
  menus: InteractionMenus;
  onReact: (reaction: Reaction) => void;
  onSoundReact: (sound: SoundReactionId) => void;
  onSoundPrefetch?: (sound: SoundReactionId) => void;
  onEmote: (animation: Animation) => void;
  myHeldItem: string;
  onHoldItem: (item: string) => void;
  onChallengeNearest: (game: ChallengeGame) => void;
  /** True while a match of mine (either game) is in flight — disables the Challenge picker. */
  challengeDisabled: boolean;
  /** Stable focus-restore target for the transient minigame result cards (owned by the HUD). */
  challengeRef: RefObject<HTMLButtonElement | null>;
  /** Whether the local player is the HOST (workspace OWNER) — gates the inline "Doors" control. */
  isHost: boolean;
  /** Live door state for the host Doors control: closed interior-door ids + whether the entry door is open. */
  doorState: { closed: string[]; entryOpen: boolean; held: string[] };
  /** The RESOLVED level's interior doorways (Phase 3) — passed through to the host Doors panel. */
  doorways: readonly Doorway[];
  /** Host-only: open/close ANY door (interior doorway id, or the entry door) from the inline Doors control. */
  onHostToggleDoor: (doorwayId: string, open: boolean) => void;
}): JSX.Element {
  const reduceMotion = useReducedMotion();
  const { registerSelector } = menus;
  // Open-state now comes from the central controller (one menu open at a time, across ALL four menus).
  const pickerOpen = menus.open === MENU.emoji;
  const soundOpen = menus.open === MENU.sound;
  const anyOpen = menus.open !== null;
  // The soundboard catalog is large (70+ memes), so a live text filter keeps it navigable.
  const [soundQuery, setSoundQuery] = useState('');
  // Toggle refs (focus-restore targets, WCAG 2.4.3) + popover/search refs (focus-on-open targets).
  const reactionsToggleRef = useRef<HTMLButtonElement>(null);
  const soundToggleRef = useRef<HTMLButtonElement>(null);
  const reactionsPopoverRef = useRef<HTMLDivElement>(null);
  const soundPopoverRef = useRef<HTMLDivElement>(null);
  const soundSearchRef = useRef<HTMLInputElement>(null);
  // Focus-on-open / restore-on-close (the SAME choreography the Animations + Hold menus use). The sound
  // menu lands focus in its search box; letters search, while DIGITS are intercepted for selection.
  useMenuFocus(pickerOpen, anyOpen, reactionsPopoverRef, reactionsToggleRef);
  useMenuFocus(soundOpen, anyOpen, soundSearchRef, soundToggleRef);
  // Never carry a stale search into the next open.
  useEffect(() => {
    if (!soundOpen) setSoundQuery('');
  }, [soundOpen]);

  // Register every menu's number-SELECTOR with the controller (1-based: number N → the N-th item). This is
  // the ONLY place the controller learns what each menu contains, so a new menu joins by adding one entry.
  // Out-of-range numbers are ignored (the controller still closes the menu on commit).
  useEffect(() => {
    registerSelector(MENU.emoji, {
      count: REACTIONS.length,
      select: (n) => {
        const r = REACTIONS[n - 1];
        if (r) onReact(r);
      },
    });
    registerSelector(MENU.sound, {
      count: SOUND_REACTIONS.length,
      select: (n) => {
        const s = SOUND_REACTIONS[n - 1];
        if (s) onSoundReact(s.id);
      },
    });
    registerSelector(MENU.animation, {
      count: EMOTES.length,
      select: (n) => {
        const e = EMOTES[n - 1];
        if (e) onEmote(e.key);
      },
    });
    registerSelector(MENU.item, {
      count: HELD_ITEMS.length,
      select: (n) => {
        const it = HELD_ITEMS[n - 1];
        if (it) onHoldItem(it.id);
      },
    });
  }, [registerSelector, onReact, onSoundReact, onEmote, onHoldItem]);

  const react = (reaction: Reaction): void => {
    onReact(reaction);
    menus.closeMenu();
  };

  const playSound = (sound: SoundReactionId): void => {
    onSoundReact(sound);
    menus.closeMenu();
  };

  // Case-insensitive filter over the VISIBLE label so the large meme catalog stays one quick type away.
  // Each surviving row keeps its ABSOLUTE 1-based number `n` (its position in the full catalog), so typing
  // "32" always plays the same sound regardless of the current filter (the spec's "sound №32"). Deliberately
  // NOT matching the raw id slug: ids strip crude/slur tokens the label hides, so matching them would
  // surface rows whose label doesn't contain the typed text and re-expose those tokens via a hidden index.
  const soundFilter = soundQuery.trim().toLowerCase();
  const soundResults = SOUND_REACTIONS.map((s, i) => ({ s, n: i + 1 })).filter(
    ({ s }) => soundFilter === '' || s.label.toLowerCase().includes(soundFilter),
  );

  return (
    <div
      role="group"
      aria-label="Express yourself"
      className="pointer-events-auto relative flex items-center gap-2 rounded-2xl border border-white/10 bg-black/50 p-2 backdrop-blur-md"
    >
      {/* Reaction picker: a "😀" toggle revealing the 8 closed-set reactions. */}
      <div className="relative">
        <button
          ref={reactionsToggleRef}
          type="button"
          // Toggle via the central controller: opening closes any other menu (only one open) and clears a
          // half-typed number; closing restores focus here the same way a pick does (WCAG 2.4.3).
          onClick={() => menus.toggleMenu(MENU.emoji)}
          aria-expanded={pickerOpen}
          aria-haspopup="true"
          aria-keyshortcuts="1"
          aria-label="Reactions"
          title="Send a quick reaction — it floats up from your duck. Press 1, then a reaction number."
          className={`flex h-10 w-10 items-center justify-center rounded-xl border text-xl transition ${
            pickerOpen
              ? 'border-amber-300/50 bg-amber-400/25'
              : 'border-white/10 bg-white/5 hover:bg-white/15'
          }`}
        >
          <span aria-hidden="true">😀</span>
        </button>
        {pickerOpen && (
          <div
            ref={reactionsPopoverRef}
            role="group"
            aria-label="Pick a reaction"
            tabIndex={-1}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.stopPropagation();
                menus.closeMenu();
              }
            }}
            className="absolute bottom-12 left-0 flex w-[min(13rem,calc(100vw-1.5rem))] flex-wrap gap-1 rounded-2xl border border-white/10 bg-black/70 p-2 outline-none backdrop-blur-md shadow-lg"
          >
            {REACTIONS.map((r, i) => (
              <button
                key={r}
                type="button"
                onClick={() => react(r)}
                aria-label={`React ${r}`}
                title={`Send ${r} — press ${i + 1}`}
                className="relative flex h-10 w-10 items-center justify-center rounded-xl bg-white/5 text-xl transition hover:scale-110 hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/50"
              >
                <span aria-hidden="true">{r}</span>
                <span
                  aria-hidden="true"
                  className="absolute bottom-0 right-0.5 text-[9px] font-bold leading-none tabular-nums text-white/45"
                >
                  {i + 1}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Soundboard (Task 6b "Sound reactions"): a "🔊" toggle revealing a compact grid of the closed-set
          catalog sounds — each plays a short, fun sound EVERYONE nearby hears (the sonic cousin of the
          emoji reactions). Same disclosure pattern as the reaction picker: aria-expanded toggle + a
          role="group" popover of dual-coded (emoji + label) buttons; one picker open at a time; Esc-close;
          focus restored to the toggle on close. Pure joy/belonging — nothing persisted, no progression. */}
      <div className="relative">
        <button
          ref={soundToggleRef}
          type="button"
          onClick={() => menus.toggleMenu(MENU.sound)}
          aria-expanded={soundOpen}
          aria-haspopup="true"
          aria-keyshortcuts="2"
          aria-label="Soundboard"
          title="Play a fun sound — everyone nearby hears it 🔊. Press 2, then a sound number."
          className={`flex h-10 w-10 items-center justify-center rounded-xl border text-xl transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/50 ${
            soundOpen
              ? 'border-amber-300/50 bg-amber-400/25'
              : 'border-white/10 bg-white/5 hover:bg-white/15'
          }`}
        >
          <span aria-hidden="true">🔊</span>
        </button>
        {soundOpen && (
          <div
            ref={soundPopoverRef}
            role="group"
            aria-label="Pick a sound"
            tabIndex={-1}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.stopPropagation();
                menus.closeMenu();
              }
            }}
            className="absolute bottom-12 left-0 flex w-[min(18rem,calc(100vw-1.5rem))] flex-col gap-2 rounded-2xl border border-white/10 bg-black/70 p-2 outline-none backdrop-blur-md shadow-lg"
          >
            {/* Search — the catalog is large (70+), so a live filter keeps it navigable (UX Director). */}
            <input
              ref={soundSearchRef}
              type="text"
              value={soundQuery}
              onChange={(e) => setSoundQuery(e.target.value)}
              placeholder="Search sounds…"
              aria-label="Search sounds"
              className="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-1.5 text-sm text-white outline-none placeholder:text-white/40 focus-visible:ring-2 focus-visible:ring-amber-300/50"
            />
            {/* Scrollable list — capped height so the long catalog never runs off the viewport.
                Focusable (tabIndex 0 + label) so keyboard users can Arrow/PageDown the long list, not
                just Tab button-by-button. */}
            <div
              tabIndex={0}
              aria-label="Sound list"
              className="flex max-h-64 flex-col gap-1 overflow-y-auto pr-1 outline-none focus-visible:ring-2 focus-visible:ring-amber-300/40"
            >
              {soundResults.length === 0 ? (
                <p className="px-2 py-4 text-center text-xs text-white/60">
                  No sounds match “{soundQuery.trim()}”.
                </p>
              ) : (
                soundResults.map(({ s, n }) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => playSound(s.id)}
                    // Warm the decode cache on hover/focus so the first click is gapless.
                    onPointerEnter={() => onSoundPrefetch?.(s.id)}
                    onFocus={() => onSoundPrefetch?.(s.id)}
                    // Fully dual-coded (emoji + text), never emoji-only; explicit accessible name per the spec.
                    aria-label={`Play sound: ${s.label}`}
                    title={`Play “${s.label}” for everyone nearby — press ${n}`}
                    className={`flex items-center gap-2 rounded-xl bg-white/5 px-2 py-2 text-left text-sm font-medium text-white transition hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/50 ${
                      reduceMotion ? '' : 'hover:scale-[1.02]'
                    }`}
                  >
                    <span aria-hidden="true" className="shrink-0 text-lg leading-none">
                      {s.icon}
                    </span>
                    <span className="min-w-0 flex-1 truncate">{s.label}</span>
                    <NumBadge n={n} />
                  </button>
                ))
              )}
            </div>
            {/* Quiet footer: reinforces the shared-moment promise + gives the filter a result count.
                role="status"/aria-live so a screen-reader hears the count change (and the implicit
                "no results") as they type into the search box (WCAG 4.1.3). */}
            <p
              role="status"
              aria-live="polite"
              className="px-1 text-center text-xs text-white/60"
            >
              {soundResults.length} sound{soundResults.length === 1 ? '' : 's'} · everyone nearby hears
              it
            </p>
          </div>
        )}
      </div>

      {/* 🎭 Animations: every emote gathered into one menu (press 3, then an animation number). */}
      <AnimationsPicker
        isOpen={menus.open === MENU.animation}
        anyOpen={anyOpen}
        onToggle={() => menus.toggleMenu(MENU.animation)}
        onClose={menus.closeMenu}
        onEmote={onEmote}
      />

      {/* ✋ Hold: a fun prop in hand — cosmetic-only (looks, never an advantage). */}
      <HoldPicker
        current={myHeldItem}
        isOpen={menus.open === MENU.item}
        anyOpen={anyOpen}
        onToggle={() => menus.toggleMenu(MENU.item)}
        onClose={menus.closeMenu}
        onHoldItem={onHoldItem}
      />

      {/* A calm divider before the social ACTION (the pickers above are self-expression; Challenge is a
          one-shot tap that reaches out to a nearby duck). */}
      <span aria-hidden="true" className="mx-0.5 h-7 w-px self-center bg-white/10" />

      {/* M7b-2 "Play Together": challenge the NEAREST duck — now a tiny 2-game picker (RPS / Bark
          Battle) sharing the universal menus controller (one popover open at a time, Esc-close,
          type-a-number). Disabled while a match of mine is already in flight so I can't double-challenge.
          Dual-coded (icon + word) + explicit accessible name; disabled reason isn't colour-only (a11y). */}
      <ChallengePicker
        menus={menus}
        disabled={challengeDisabled}
        toggleRef={challengeRef}
        onChallenge={onChallengeNearest}
      />

      {/* Host-only "Doors" control (Room Doors): the host's reach to open/close ANY door from anywhere,
          sitting right of Challenge in this central action bar. Shares the `menus` controller (one popover
          open at a time + Esc-close), and its list pops up ABOVE the row. */}
      {isHost && <HostDoorsPanel menus={menus} doorState={doorState} doorways={doorways} onToggle={onHostToggleDoor} />}
    </div>
  );
}

/** The two Challenge games, in picker order (their 1-based index is the type-a-number shortcut). */
const CHALLENGE_GAMES: ReadonlyArray<{
  id: ChallengeGame;
  icon: string;
  name: string;
  blurb: string;
}> = [
  { id: 'rps', icon: '🪨📄✂️', name: 'Rock-Paper-Scissors', blurb: 'Best throw wins' },
  { id: 'bark', icon: '🐶', name: 'Bark Battle', blurb: 'Out-bark them for 10 seconds — mic on!' },
];

/**
 * The Challenge GAME PICKER (M7b-2 + Challenge game #2). The old one-tap Challenge button became a
 * two-item popover the moment there was a second game: pick Rock-Paper-Scissors or the Bark Battle
 * and the parent challenges the NEAREST duck to it. Shares the universal menus controller (only one
 * popover open across the bar, Esc-close, focus-on-open/restore-on-close) and registers a selector so
 * type-a-number picks a game like every other menu. Dual-coded throughout; never colour/emoji-alone.
 */
function ChallengePicker({
  menus,
  disabled,
  toggleRef,
  onChallenge,
}: {
  menus: InteractionMenus;
  disabled: boolean;
  /** The toggle doubles as the stable focus-restore target for the minigame result cards. */
  toggleRef: RefObject<HTMLButtonElement | null>;
  onChallenge: (game: ChallengeGame) => void;
}): JSX.Element {
  const isOpen = menus.open === MENU.challenge;
  const anyOpen = menus.open !== null;
  const popoverRef = useRef<HTMLDivElement>(null);
  useMenuFocus(isOpen, anyOpen, popoverRef, toggleRef);

  const { closeMenu, registerSelector } = menus;
  const pick = useCallback(
    (game: ChallengeGame): void => {
      onChallenge(game);
      closeMenu();
    },
    [onChallenge, closeMenu],
  );
  // Join the type-a-number system: while this popover is open, typing 1/2 picks the game.
  useEffect(() => {
    registerSelector(MENU.challenge, {
      count: CHALLENGE_GAMES.length,
      select: (n) => {
        const game = CHALLENGE_GAMES[n - 1];
        if (game) pick(game.id);
      },
    });
  }, [registerSelector, pick]);

  return (
    <div className="relative">
      <button
        ref={toggleRef}
        type="button"
        onClick={() => menus.toggleMenu(MENU.challenge)}
        disabled={disabled}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label={
          disabled
            ? 'Challenge — finish your current match first'
            : 'Challenge the nearest duck — pick a game'
        }
        title={
          disabled
            ? 'You’re already in a match — finish it first 🦆'
            : 'Challenge the nearest duck — Rock-Paper-Scissors or a Bark Battle 🦆'
        }
        className={`flex h-10 items-center gap-1.5 rounded-xl border px-3 text-sm font-medium text-amber-100 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/60 disabled:cursor-not-allowed disabled:opacity-50 ${
          isOpen
            ? 'border-amber-300/60 bg-amber-400/30'
            : 'border-amber-300/40 bg-amber-400/15 hover:bg-amber-400/25'
        }`}
      >
        <span aria-hidden="true">🪨📄✂️</span>
        <span className="hidden sm:inline">Challenge</span>
      </button>
      {isOpen && (
        <div
          ref={popoverRef}
          role="group"
          aria-label="Pick a game to challenge the nearest duck to"
          tabIndex={-1}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.stopPropagation();
              closeMenu();
            }
          }}
          className="absolute bottom-12 left-1/2 flex w-[min(17rem,calc(100vw-1.5rem))] -translate-x-1/2 flex-col gap-1.5 rounded-2xl border border-white/10 bg-black/70 p-2 outline-none backdrop-blur-md shadow-lg"
        >
          {CHALLENGE_GAMES.map((game, i) => (
            <button
              key={game.id}
              type="button"
              onClick={() => pick(game.id)}
              aria-label={`Challenge to ${game.name}`}
              title={`${game.name} — press ${i + 1}`}
              className="flex items-center gap-2 rounded-xl bg-white/5 px-2.5 py-2 text-left text-sm font-medium text-white transition hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/50"
            >
              <span aria-hidden="true" className="text-lg leading-none">{game.icon}</span>
              <span className="min-w-0 flex-1">
                <span className="block truncate">{game.name}</span>
                <span className="block truncate text-xs font-normal text-white/60">{game.blurb}</span>
              </span>
              <NumBadge n={i + 1} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Resizable chat window (MMO-style) ──────────────────────────────────────────────────────────────
/** localStorage key for the player's chosen chat-window size (persists across reloads). */
const CHAT_SIZE_KEY = 'duck.chat.size';
/** Size bounds (px). Min keeps it usable; max keeps a dragged window from swamping the world. */
const CHAT_MIN = { w: 248, h: 132 };
const CHAT_MAX = { w: 480, h: 440 };
/** The comfortable default — bigger than the old 18rem strip so the banter has room to breathe. */
const CHAT_DEFAULT = { w: 344, h: 212 };
/** A slightly tighter default below lg so the chat doesn't dominate a tablet screen out of the box. */
const CHAT_DEFAULT_COMPACT = { w: 288, h: 184 };

/**
 * The chat window lives bottom-LEFT while the action bar is centred at the bottom. To guarantee they
 * never collide, cap the chat's width so its right edge always clears the action bar's left edge: reserve
 * half the action-bar footprint (~200px) plus a comfortable gap from the screen centre. Height is capped
 * to ~60vh so a tall persisted size never runs off a short viewport. Falls back to the static CHAT_MAX
 * when the viewport is unknown (SSR / first paint).
 */
function chatMaxFor(vw: number, vh: number): { w: number; h: number } {
  // Keep the chat fully on the left half minus a gap, and never past the centred action bar's left edge.
  const widthBudget = vw > 0 ? Math.floor(vw / 2 - 24) : CHAT_MAX.w;
  const w = Math.max(CHAT_MIN.w, Math.min(CHAT_MAX.w, widthBudget));
  const h = vh > 0 ? Math.max(CHAT_MIN.h, Math.min(CHAT_MAX.h, Math.floor(vh * 0.6))) : CHAT_MAX.h;
  return { w, h };
}
const clampChat = (
  w: number,
  h: number,
  max: { w: number; h: number } = CHAT_MAX,
): { w: number; h: number } => ({
  w: Math.max(CHAT_MIN.w, Math.min(max.w, Math.round(w))),
  h: Math.max(CHAT_MIN.h, Math.min(max.h, Math.round(h))),
});

/**
 * Render a spoken chat body with SAFE auto-linkified URLs. Each segment from {@link parseChatSegments}
 * becomes either an escaped React text node or a hardened `<a>` (sanitized http/https href, `rel="noopener
 * noreferrer nofollow ugc"`, opens in a new tab). We never set innerHTML and only http/https survive the
 * parser, so a link a citizen pastes can't inject markup or carry a `javascript:`/`data:` payload.
 */
function ChatBody({ text }: { text: string }): JSX.Element {
  const segments = useMemo(() => parseChatSegments(text), [text]);
  return (
    <>
      {segments.map((seg, i) =>
        seg.kind === 'link' ? (
          <a
            key={i}
            href={seg.href}
            target="_blank"
            rel="noopener noreferrer nofollow ugc"
            title={seg.href}
            className="break-all font-medium text-sky-300 underline decoration-sky-300/40 underline-offset-2 transition hover:text-sky-200"
          >
            {seg.label}
          </a>
        ) : (
          <span key={i}>{seg.value}</span>
        ),
      )}
    </>
  );
}

/**
 * One rendered chat line. The speaker's name is BOLD + tinted with their CHARACTER colour (dual-coded with
 * the weight, so colour is never the only "who" signal); a spoken body auto-linkifies URLs. A hover/tap
 * "copy" button copies the readable line — and the text is natively selectable too. The copy control is a
 * mouse convenience (aria-hidden + not tabbable): keyboard/SR users copy via selection, which keeps the
 * surrounding role="log" aria-live region free of per-line control chatter.
 */
// memo (perf-audit R2): chat lines are stable by id, so a new line re-renders only its own row —
// the existing ≤50 rows (incl. their URL link-parsing in ChatBody) are not re-rendered on each arrival.
const ChatLineRow = memo(function ChatLineRow({
  line,
  onReplay,
}: {
  line: ChatLine;
  /** Re-play this line's sound for the whole room (routes through the soundboard broadcast path). */
  onReplay: (id: SoundReactionId) => void;
}): JSX.Element {
  if (import.meta.env.DEV) bumpRender('chatRow'); // perf-audit M4 (R2): only the new line's row bumps
  const [copied, setCopied] = useState(false);
  const readable =
    line.name === undefined
      ? line.text
      : line.kind === 'say'
        ? `${line.name}: ${line.text}`
        : `${line.name} ${line.text}`;
  const copy = (): void => {
    try {
      void navigator.clipboard?.writeText(readable);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    } catch {
      /* clipboard blocked — native text selection still works */
    }
  };
  return (
    <div className="group relative py-0.5 pr-6">
      <span className="select-text break-words">
        {line.name !== undefined && (
          <>
            <span className="font-bold" style={{ color: line.color }}>
              {line.name}
            </span>
            <span className="text-white/40">{line.kind === 'say' ? ': ' : ' '}</span>
          </>
        )}
        {line.kind === 'say' ? <ChatBody text={line.text} /> : <span>{line.text}</span>}
        {line.soundId !== undefined && (
          <button
            type="button"
            onClick={() => onReplay(line.soundId as SoundReactionId)}
            onPointerEnter={() => prefetchSoundReaction(line.soundId as SoundReactionId)}
            onFocus={() => prefetchSoundReaction(line.soundId as SoundReactionId)}
            title="Play this sound again for the room"
            aria-label="Play this sound again for the room"
            className="ml-1 inline-flex items-center rounded px-1 text-[12px] leading-none text-white/60 align-baseline transition hover:bg-white/10 hover:text-white/90 focus-visible:outline focus-visible:outline-1 focus-visible:outline-white/60"
          >
            🔊
          </button>
        )}
      </span>
      <button
        type="button"
        aria-hidden="true"
        tabIndex={-1}
        onClick={copy}
        title={copied ? 'Copied!' : 'Copy message'}
        className="absolute right-0 top-0.5 rounded px-1 text-[11px] leading-none text-white/30 opacity-0 transition hover:text-white/80 group-hover:opacity-100"
      >
        {copied ? '✓' : '⧉'}
      </button>
    </div>
  );
});

/**
 * The MMO-style CHAT WINDOW (M7a "Socialize", bottom-left). It pairs the accessible "recent chatter" LOG
 * with the speech INPUT (moved here from the action bar) into one proper chat window — bigger than the old
 * strip, and RESIZABLE: drag the top-right grip, or focus it and use the arrow keys, to grow it like a
 * real MMO chat. The chosen size persists across reloads.
 *
 * The chatter is still strictly EPHEMERAL — in-memory only, never persisted or logged (Constitution
 * Art.1: banter, never a surveillance log). The window just lets the last little while of it breathe.
 *
 * Accessibility: the log keeps `role="log"` + `aria-live="polite"` + the "Recent chatter" name (the e2e
 * + SR target); the input keeps `aria-label="Say something"` and the "Send message" button; the resize
 * grip is a focusable button with an explicit name and arrow-key control. Anchored bottom-left so growing
 * the height extends it UPWARD (never off the bottom of the screen).
 */
function ChatPanel({
  lines,
  onSay,
  onReplaySound,
}: {
  lines: ChatLine[];
  onSay: (text: string) => void;
  /** Re-play a chat line's sound for the whole room (soundboard broadcast path). */
  onReplaySound: (id: SoundReactionId) => void;
}): JSX.Element {
  const [draft, setDraft] = useState('');
  // The chat window is viewport-aware (Defect D): its max width/height clamp to the current screen so a
  // large persisted size can never dominate a small screen or collide with the centred action bar.
  const { width: vw, height: vh } = useViewport();
  const { w: maxW, h: maxH } = chatMaxFor(vw, vh);
  const chatMax = { w: maxW, h: maxH };
  const [size, setSize] = useState<{ w: number; h: number }>(() => {
    const fallback = typeof window !== 'undefined' && window.innerWidth < 768 ? CHAT_DEFAULT_COMPACT : CHAT_DEFAULT;
    const max = typeof window !== 'undefined' ? chatMaxFor(window.innerWidth, window.innerHeight) : CHAT_MAX;
    try {
      const raw = localStorage.getItem(CHAT_SIZE_KEY);
      if (raw) {
        const p = JSON.parse(raw) as { w?: unknown; h?: unknown };
        if (typeof p.w === 'number' && typeof p.h === 'number') return clampChat(p.w, p.h, max);
      }
    } catch {
      /* ignore a malformed/blocked store — fall back to the default */
    }
    return clampChat(fallback.w, fallback.h, max);
  });
  // Re-clamp to the viewport when it changes (rotate / resize), so the window can't overflow or collide
  // after a resize. We DON'T persist the clamped-down value — the player's chosen size is restored if the
  // screen grows back. Only shrinks toward fit; never grows past what the player picked.
  useEffect(() => {
    setSize((prev) => {
      const next = clampChat(prev.w, prev.h, { w: maxW, h: maxH });
      return next.w === prev.w && next.h === prev.h ? prev : next;
    });
  }, [maxW, maxH]);
  const logRef = useRef<HTMLDivElement>(null);
  // Keep the freshest line in view (MMO chat scrolls to the bottom as banter arrives).
  useEffect(() => {
    const el = logRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [lines]);
  // Latest size in a ref so the pointer-up persist reads the final value (not a stale drag-start closure).
  const sizeRef = useRef(size);
  sizeRef.current = size;
  const persist = (s: { w: number; h: number }): void => {
    try {
      localStorage.setItem(CHAT_SIZE_KEY, JSON.stringify(s));
    } catch {
      /* ignore a blocked store */
    }
  };

  // Pointer-drag resize off the top-right grip: rightward grows width, upward grows height (the window is
  // anchored bottom-left, so height extends upward). Pointer capture keeps the drag glued to the grip.
  const dragRef = useRef<{ x: number; y: number; w: number; h: number } | null>(null);
  const onGripPointerDown = (e: ReactPointerEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    dragRef.current = { x: e.clientX, y: e.clientY, w: size.w, h: size.h };
  };
  const onGripPointerMove = (e: ReactPointerEvent<HTMLButtonElement>): void => {
    const d = dragRef.current;
    if (!d) return;
    setSize(clampChat(d.w + (e.clientX - d.x), d.h - (e.clientY - d.y), chatMax));
  };
  const onGripPointerUp = (e: ReactPointerEvent<HTMLButtonElement>): void => {
    if (!dragRef.current) return;
    dragRef.current = null;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    persist(sizeRef.current);
  };
  // Keyboard resize (a11y): arrow keys nudge the size in calm steps when the grip is focused.
  const onGripKeyDown = (e: ReactKeyboardEvent<HTMLButtonElement>): void => {
    const STEP = 24;
    let { w, h } = size;
    if (e.key === 'ArrowRight') w += STEP;
    else if (e.key === 'ArrowLeft') w -= STEP;
    else if (e.key === 'ArrowUp') h += STEP;
    else if (e.key === 'ArrowDown') h -= STEP;
    else return;
    e.preventDefault();
    const next = clampChat(w, h, chatMax);
    setSize(next);
    persist(next);
  };

  const send = (): void => {
    const text = draft.trim();
    if (!text) return;
    onSay(text);
    setDraft(''); // never persisted — just clear the box
  };

  return (
    <div
      className="pointer-events-auto flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-slate-950/70 shadow-xl backdrop-blur-md"
      style={{ width: size.w, height: size.h }}
    >
      {/* Header: a calm title + the resize grip. */}
      <div className="flex items-center justify-between gap-2 border-b border-white/10 px-3 py-1.5">
        <span className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-white/45">
          <span aria-hidden="true">💬</span> Chat
        </span>
        <button
          type="button"
          aria-label="Resize chat window — drag, or use the arrow keys"
          title="Drag to resize (or focus and use the arrow keys)"
          onPointerDown={onGripPointerDown}
          onPointerMove={onGripPointerMove}
          onPointerUp={onGripPointerUp}
          onKeyDown={onGripKeyDown}
          className="cursor-nwse-resize touch-none rounded-md px-1 text-sm leading-none text-white/30 transition hover:text-white/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/50"
        >
          <span aria-hidden="true">⤡</span>
        </button>
      </div>
      {/* The accessible chatter log — scrolls, grows with the window, EPHEMERAL (never saved). */}
      <div
        ref={logRef}
        role="log"
        aria-live="polite"
        aria-label="Recent chatter"
        className="min-h-0 flex-1 overflow-y-auto px-3 py-2 text-xs leading-relaxed text-white/80"
      >
        {lines.length === 0 ? (
          <p className="text-white/35">
            Say hi to the office… 🦆 Banter floats here for a bit — never saved.
          </p>
        ) : (
          lines.map((line) => (
            <ChatLineRow key={line.id} line={line} onReplay={onReplaySound} />
          ))
        )}
      </div>
      {/* Speech input (moved here from the action bar): Enter sends, Esc blurs back to the world. */}
      <div className="flex items-center gap-2 border-t border-white/10 p-2">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              send();
            } else if (e.key === 'Escape') {
              e.currentTarget.blur();
            }
          }}
          maxLength={SAY_MAX_LEN}
          aria-label="Say something"
          placeholder="Say something…"
          className="min-w-0 flex-1 rounded-xl border border-white/10 bg-black/40 px-3 py-2 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-amber-300/50"
        />
        <button
          type="button"
          onClick={send}
          disabled={draft.trim().length === 0}
          aria-label="Send message"
          title="Send (Enter)"
          className="flex shrink-0 items-center gap-1 rounded-xl border border-amber-300/40 bg-amber-400/15 px-3 py-2 text-sm font-medium text-amber-100 transition hover:bg-amber-400/25 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <span aria-hidden="true">💬</span> Send
        </button>
      </div>
    </div>
  );
}

/**
 * The three Rock-Paper-Scissors choice buttons (M7b-2 "Play Together"). Shared by the invite dialog and
 * the in-match card so the accessible names + look never drift. Each button is DUAL-CODED (icon + word
 * from RPS_CHOICE_LABELS) and keyboard-reachable with an explicit accessible name — "Play Rock",
 * "Play Paper", "Play Scissors" — so it's never icon/colour-only. `autoFocusFirst` lands focus on the
 * first choice when the dialog opens (focus management). Disabled the instant a pick is made so a double
 * tap can't double-send.
 */
function RpsChoiceButtons({
  onChoose,
  disabled,
  firstChoiceRef,
}: {
  onChoose: (choice: RpsChoice) => void;
  disabled?: boolean;
  firstChoiceRef?: RefObject<HTMLButtonElement | null>;
}): JSX.Element {
  return (
    <div role="group" aria-label="Your choice" className="flex flex-wrap gap-2">
      {RPS_CHOICES.map((choice, i) => {
        const { icon, label } = RPS_CHOICE_LABELS[choice];
        return (
          <button
            key={choice}
            ref={i === 0 ? firstChoiceRef : undefined}
            type="button"
            onClick={() => onChoose(choice)}
            disabled={disabled}
            aria-label={`Play ${label}`}
            title={`Play ${label}`}
            className="flex items-center gap-1.5 rounded-xl border border-amber-300/40 bg-amber-400/15 px-3 py-2 text-sm font-semibold text-amber-100 transition hover:bg-amber-400/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/60 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span aria-hidden="true" className="text-lg">{icon}</span>
            <span>{label}</span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * RPS invite prompt (M7b-2 "Play Together", emotion: Tradition). A friendly, FOCUS-MANAGED dialog
 * shown when someone challenges me: "{fromName} challenged you to Rock-Paper-Scissors!" with the three
 * choice buttons (icon + word) and a "Decline" button. Picking a choice sends RPS_CHOOSE; Decline (or
 * Esc) sends RPS_DECLINE — a gentle "no thanks", never a penalty.
 *
 * Accessibility (mirrors the GuildPanel/PeoplePanel focus pattern):
 *   • role="dialog" + aria-modal, labelled by its heading + described by the blurb.
 *   • Focus-on-open (lands on the first choice) + restore-to-opener on close (WCAG 2.4.3).
 *   • Esc declines (a clear, expected way out). NOT a full focus-trap — the world stays interactive
 *     behind it (calm + non-blocking), consistent with the rest of the HUD.
 *   • Dual-coded throughout (icon + text), never colour/emoji-alone.
 */
function RpsInviteDialog({
  invite,
  onChoose,
  onDecline,
}: {
  invite: RpsInvitePrompt;
  onChoose: (choice: RpsChoice) => void;
  onDecline: () => void;
}): JSX.Element {
  const firstChoiceRef = useRef<HTMLButtonElement>(null);
  // FIX 2 — a rapid double-tap must not fire two RPS_CHOOSE. Latch the instant a pick is sent and
  // disable the choice buttons; keyed by matchId so a fresh invite (new match) re-arms cleanly. The
  // parent flips this dialog to the in-match 'waiting' card on choose, so this is belt-and-braces for
  // the brief same-frame window before that swap lands.
  const [chosen, setChosen] = useState(false);
  useEffect(() => setChosen(false), [invite.matchId]);
  const choose = (choice: RpsChoice): void => {
    if (chosen) return;
    setChosen(true);
    onChoose(choice);
  };

  // Focus-on-open + restore-on-close (WCAG 2.4.3), mirroring PeoplePanel. Remember whatever had focus
  // when the invite arrived (the challenge button, or wherever the player was) and pull focus to the
  // first choice; on close, hand it back. Keyed by matchId so a fresh invite re-runs this cleanly.
  //
  // FIX 5 — DON'T steal focus while the first-run WelcomeModal is open. The modal owns focus + its own
  // Esc; if an invite lands mid-onboarding, yanking focus to this (visually-behind) dialog and double-
  // binding Esc would be jarring + create a dual-modal conflict. Decoupled contract: the WelcomeModal
  // sets `data-welcome-open` on <body> while open; we read it here (no import, safe if it never appears).
  // The dialog still RENDERS — it just waits politely. We only restore focus on close if we actually
  // stole it, so a no-steal path never tugs focus away from the modal.
  useEffect(() => {
    if (document.body.hasAttribute('data-welcome-open')) return undefined;
    const opener = document.activeElement as HTMLElement | null;
    firstChoiceRef.current?.focus();
    return () => {
      if (opener && opener.isConnected && typeof opener.focus === 'function') opener.focus();
    };
  }, [invite.matchId]);

  // Esc declines — a clear, expected way out (in addition to the Decline button). FIX 5: while the
  // WelcomeModal is open it owns Esc, so we defer — let the modal handle Esc and avoid declining the
  // invite out from under an onboarding player (no dual-modal Esc conflict). Re-checked per keypress, so
  // the moment the modal closes Esc declines again as normal.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        if (document.body.hasAttribute('data-welcome-open')) return;
        e.preventDefault();
        onDecline();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onDecline]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="rps-invite-title"
      aria-describedby="rps-invite-desc"
      className="pointer-events-auto w-[min(20rem,calc(100vw-1.5rem))] rounded-2xl border border-amber-300/40 bg-black/70 p-4 backdrop-blur-md shadow-2xl"
    >
      <div className="flex items-center gap-2">
        <span aria-hidden="true" className="text-2xl">🪨📄✂️</span>
        <h2 id="rps-invite-title" className="text-sm font-bold text-amber-100">
          Rock-Paper-Scissors!
        </h2>
      </div>
      <p id="rps-invite-desc" className="mt-1 text-xs leading-snug text-white/80">
        <span className="font-semibold text-white">{invite.fromName}</span> challenged you. Pick one to
        play — win or lose, it’s just for fun. 🦆
      </p>
      <div className="mt-3">
        <RpsChoiceButtons onChoose={choose} disabled={chosen} firstChoiceRef={firstChoiceRef} />
      </div>
      <div className="mt-3 flex justify-end">
        <button
          type="button"
          onClick={onDecline}
          aria-label="Decline challenge"
          title="No thanks (Esc) — a friendly pass, never held against you"
          className="rounded-xl border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-medium text-white/80 transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
        >
          Decline
        </button>
      </div>
    </div>
  );
}

/**
 * In-match RPS card (M7b-2) for when I'm in a match but the invite dialog isn't the active surface —
 * i.e. I challenged someone (and the server is prompting me to choose), or I've already picked and am
 * waiting for my opponent. Two phases:
 *   • 'choosing' — show the three choice buttons (e.g. the challenger picking their move).
 *   • 'waiting'  — "Waiting for {opponent}…", echoing what I played. Dual-coded, reduced-motion safe.
 */
function RpsMatchCard({
  match,
  onChoose,
}: {
  match: RpsMatchView;
  onChoose: (choice: RpsChoice) => void;
}): JSX.Element {
  const reduceMotion = useReducedMotion();
  // FIX 2 — a rapid double-tap must not fire two RPS_CHOOSE. Latch the instant a pick is sent and
  // disable the choice buttons; keyed by matchId so a new match re-arms cleanly. The parent flips this
  // card to the 'waiting' phase on choose, so this guards the brief same-frame window before that swap.
  const [chosen, setChosen] = useState(false);
  useEffect(() => setChosen(false), [match.matchId]);
  const choose = (choice: RpsChoice): void => {
    if (chosen) return;
    setChosen(true);
    onChoose(choice);
  };
  if (match.phase === 'waiting') {
    const played = match.myChoice ? RPS_CHOICE_LABELS[match.myChoice] : null;
    return (
      <div
        role="status"
        aria-live="polite"
        aria-label={`You played ${played ? played.label : 'your move'}. Waiting for ${match.opponentName} to choose.`}
        className="pointer-events-auto flex items-center gap-2 rounded-2xl border border-amber-300/40 bg-amber-400/15 px-4 py-2.5 text-sm font-medium text-amber-100 backdrop-blur-md shadow-lg"
      >
        <span
          aria-hidden="true"
          className={`h-2 w-2 rounded-full bg-amber-300 ${reduceMotion ? '' : 'animate-ping'}`}
        />
        <span aria-hidden="true" className="text-lg">{played ? played.icon : '🪨'}</span>
        <span>
          Waiting for <span className="font-semibold">{match.opponentName}</span>…
        </span>
      </div>
    );
  }
  // 'choosing'
  return (
    <div
      role="group"
      aria-label={`Rock-Paper-Scissors with ${match.opponentName}`}
      className="pointer-events-auto flex flex-col gap-2 rounded-2xl border border-amber-300/40 bg-black/60 p-3 backdrop-blur-md shadow-lg"
    >
      <div className="text-xs font-medium text-amber-100">
        <span aria-hidden="true">🪨📄✂️</span> Playing{' '}
        <span className="font-semibold">{match.opponentName}</span> — pick one!
      </div>
      <RpsChoiceButtons onChoose={choose} disabled={chosen} />
    </div>
  );
}

/** Warm, dual-coded result copy from MY point of view (M7b-2). Choices are present only when we played. */
function rpsResultText(r: RpsResultView): { headline: string; detail: string; icon: string } {
  const mine = r.yourChoice ? RPS_CHOICE_LABELS[r.yourChoice] : null;
  const theirs = r.theirChoice ? RPS_CHOICE_LABELS[r.theirChoice] : null;
  const played = mine && theirs ? `You ${mine.icon} vs ${theirs.icon} ${r.opponentName}` : '';
  switch (r.outcome) {
    case 'win':
      return { icon: '🎉', headline: `You won — gg, ${r.opponentName}!`, detail: played };
    case 'lose':
      return { icon: '🤝', headline: `${r.opponentName} won — gg!`, detail: played };
    case 'draw':
      return { icon: '🤝', headline: 'A draw — great minds!', detail: played };
    case 'declined':
      return { icon: '🦆', headline: 'Maybe next time', detail: `${r.opponentName} passed for now.` };
    case 'expired':
      return { icon: '⌛', headline: 'The challenge fizzled out', detail: 'No one picked in time.' };
    default:
      return { icon: '🦆', headline: 'Match ended', detail: '' };
  }
}

/**
 * Warm, transient RPS result card (M7b-2). Shows the outcome FROM MY point of view with both choices
 * ("You ✊ vs 📄 — gg!"); a win gets a little celebration (the parent ALSO fires a CELEBRATE burst).
 * declined/expired read gently. role="status" aria-live="polite" announces it to screen readers; a
 * Dismiss button (and the parent's auto-clear) tidies it away. Dual-coded + reduced-motion safe.
 */
function RpsResultCard({
  result,
  onDismiss,
  restoreFocusRef,
}: {
  result: RpsResultView;
  onDismiss: () => void;
  /** A STABLE element (the Challenge button) to hand focus back to when the card unmounts (FIX 4). */
  restoreFocusRef?: RefObject<HTMLButtonElement | null>;
}): JSX.Element {
  const reduceMotion = useReducedMotion();
  const { icon, headline, detail } = rpsResultText(result);
  const enter = reduceMotion ? {} : { initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0 } };
  // FIX 4 — focus management (WCAG 2.4.3), mirroring the invite-dialog/PeoplePanel pattern. The card
  // REPLACES the match/invite surface whose focused choice button has just unmounted (focus → <body>),
  // so on show we pull focus to the Dismiss control; on unmount (auto-clear OR Dismiss) we hand focus to
  // a stable element (the Challenge button) so it never falls to <body>. Keyed on mount via [] — a new
  // result remounts the card (the parent keys it by matchId), so this re-runs cleanly per result.
  const dismissRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    dismissRef.current?.focus();
    // Capture the restore target up front: the Challenge button is a STABLE, always-mounted node, so the
    // node we'd hand focus back to on unmount is the same one present now (and this avoids the lint warning
    // about reading a ref's .current in cleanup).
    const restore = restoreFocusRef?.current ?? null;
    return () => {
      if (restore && restore.isConnected && typeof restore.focus === 'function') restore.focus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <motion.div
      {...enter}
      role="status"
      aria-live="polite"
      aria-label={detail ? `${headline} ${detail}` : headline}
      className="pointer-events-auto flex items-center gap-3 rounded-2xl border border-amber-300/40 bg-amber-400/15 px-4 py-2.5 text-amber-100 backdrop-blur-md shadow-lg"
    >
      <span aria-hidden="true" className="text-2xl">{icon}</span>
      <div className="leading-tight">
        <div className="text-sm font-bold">{headline}</div>
        {detail && <div className="text-xs text-amber-100/80">{detail}</div>}
      </div>
      <button
        ref={dismissRef}
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss result"
        title="Dismiss"
        className="ml-1 rounded-lg border border-white/10 bg-white/5 px-2 py-1 text-xs font-medium text-white/80 transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
      >
        ✕
      </button>
    </motion.div>
  );
}

// ── Bark Battle surfaces (Challenge game #2 — contracts domain/bark.ts) ────────────────────────────

/** Why the local mic can't score right now, or null when it's good to bark. */
type BarkMicIssue = 'off' | 'muted' | null;

/**
 * A small, insistent-but-kind mic warning used by the Bark Battle surfaces: a muted (or off) mic
 * scores ZERO, and that would be a sad way to lose. One action button fixes the state (enable voice /
 * unmute); PTT players are reminded of the hold-to-talk key instead of being told they're "muted".
 */
function BarkMicWarning({
  issue,
  pttEnabled,
  onFix,
}: {
  issue: Exclude<BarkMicIssue, null>;
  pttEnabled: boolean;
  onFix: () => void;
}): JSX.Element {
  const copy =
    issue === 'off'
      ? 'Voice is off — your barks would score 0!'
      : pttEnabled
        ? 'Hold your talk key while you bark — or switch push-to-talk off!'
        : 'You’re muted — your barks would score 0!';
  const action = issue === 'off' ? 'Enable voice' : pttEnabled ? null : 'Unmute';
  return (
    <div
      role="alert"
      className="flex items-center gap-2 rounded-xl border border-rose-300/40 bg-rose-500/15 px-2.5 py-1.5 text-xs font-medium text-rose-100"
    >
      <span aria-hidden="true">🎤</span>
      <span className="min-w-0 flex-1">{copy}</span>
      {action && (
        <button
          type="button"
          onClick={onFix}
          className="shrink-0 rounded-lg border border-rose-300/40 bg-rose-400/20 px-2 py-1 font-semibold transition hover:bg-rose-400/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-300/60"
        >
          {action}
        </button>
      )}
    </div>
  );
}

/**
 * Bark Battle invite prompt (Challenge game #2). The same friendly, FOCUS-MANAGED dialog choreography
 * as {@link RpsInviteDialog} (focus-on-open → Accept, restore-on-close, Esc declines, WelcomeModal
 * guard), but the accept is a single "Let's bark!" button — the game itself needs nothing but lungs.
 * If the mic can't score right now the dialog says so up front (accepting is still allowed — the
 * battle card keeps offering the fix through the countdown).
 */
function BarkInviteDialog({
  invite,
  micIssue,
  pttEnabled,
  onFixMic,
  onAccept,
  onDecline,
}: {
  invite: BarkInvitePrompt;
  micIssue: BarkMicIssue;
  pttEnabled: boolean;
  onFixMic: () => void;
  onAccept: () => void;
  onDecline: () => void;
}): JSX.Element {
  const acceptRef = useRef<HTMLButtonElement>(null);
  // Double-tap latch (same FIX 2 as RPS): one BARK_ACCEPT per invite, re-armed per matchId.
  const [accepted, setAccepted] = useState(false);
  useEffect(() => setAccepted(false), [invite.matchId]);
  const accept = (): void => {
    if (accepted) return;
    setAccepted(true);
    onAccept();
  };

  // Focus-on-open + restore-on-close (WCAG 2.4.3) with the same WelcomeModal deference as RPS (FIX 5).
  useEffect(() => {
    if (document.body.hasAttribute('data-welcome-open')) return undefined;
    const opener = document.activeElement as HTMLElement | null;
    acceptRef.current?.focus();
    return () => {
      if (opener && opener.isConnected && typeof opener.focus === 'function') opener.focus();
    };
  }, [invite.matchId]);

  // Esc declines (deferred while the WelcomeModal owns Esc — FIX 5).
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        if (document.body.hasAttribute('data-welcome-open')) return;
        e.preventDefault();
        onDecline();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onDecline]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="bark-invite-title"
      aria-describedby="bark-invite-desc"
      className="pointer-events-auto w-[min(20rem,calc(100vw-1.5rem))] rounded-2xl border border-amber-300/40 bg-black/70 p-4 backdrop-blur-md shadow-2xl"
    >
      <div className="flex items-center gap-2">
        <span aria-hidden="true" className="text-2xl">🐶</span>
        <h2 id="bark-invite-title" className="text-sm font-bold text-amber-100">
          Bark Battle!
        </h2>
      </div>
      <p id="bark-invite-desc" className="mt-1 text-xs leading-snug text-white/80">
        <span className="font-semibold text-white">{invite.fromName}</span> challenges you to a Bark
        Battle: bark as LOUD and as often as you can for 10 seconds — the loudest duck wins. The whole
        room will hear you. 🐶
      </p>
      {micIssue && (
        <div className="mt-2">
          <BarkMicWarning issue={micIssue} pttEnabled={pttEnabled} onFix={onFixMic} />
        </div>
      )}
      <div className="mt-3 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onDecline}
          aria-label="Decline Bark Battle"
          title="No thanks (Esc) — a friendly pass, never held against you"
          className="rounded-xl border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-medium text-white/80 transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
        >
          Decline
        </button>
        <button
          ref={acceptRef}
          type="button"
          onClick={accept}
          disabled={accepted}
          aria-label="Accept Bark Battle"
          title="Accept — 3-2-1, then BARK!"
          className="rounded-xl border border-amber-300/40 bg-amber-400/15 px-3 py-1.5 text-xs font-semibold text-amber-100 transition hover:bg-amber-400/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/60 disabled:opacity-50"
        >
          🐶 Let’s bark!
        </button>
      </div>
    </div>
  );
}

/**
 * The LIVE Bark Battle scoreboard — the ONLY consumer of the isolated bark-score store, so the ~10 Hz
 * BARK_TICK repaints just this leaf (perf-audit R1 lesson), never the whole HUD. A tug-of-war bar
 * (my share vs theirs) + both running totals + the window clock. Deliberately NOT aria-live: ten
 * updates a second would flood a screen reader — the battle announces its start and its RESULT, and
 * the group label tells AT users what the region is.
 */
function BarkScoreboard({ opponentName }: { opponentName: string }): JSX.Element {
  const reduceMotion = useReducedMotion();
  const score = useSyncExternalStore(subscribeBarkScore, getBarkScore);
  const total = score.myScore + score.theirScore;
  const myShare = total === 0 ? 0.5 : score.myScore / total;
  const secondsLeft = Math.max(0, Math.ceil(score.remainingMs / 1000));
  return (
    <div role="group" aria-label={`Live bark scoreboard against ${opponentName}`}>
      <div className="flex items-baseline justify-between gap-3 text-sm font-bold tabular-nums">
        <span className="text-amber-100">
          You <span className="text-lg">{score.myScore}</span>
        </span>
        <span aria-hidden="true" className="text-xs font-semibold text-white/60">
          ⏱ {secondsLeft}s
        </span>
        <span className="text-white/90">
          <span className="text-lg">{score.theirScore}</span> {opponentName}
        </span>
      </div>
      <div
        aria-hidden="true"
        className="mt-1.5 flex h-2.5 w-full overflow-hidden rounded-full border border-white/10 bg-white/10"
      >
        <div
          className={`h-full rounded-l-full bg-amber-400 ${reduceMotion ? '' : 'transition-[width] duration-150 ease-out'}`}
          style={{ width: `${Math.round(myShare * 1000) / 10}%` }}
        />
        <div className="h-full flex-1 rounded-r-full bg-sky-400/70" />
      </div>
    </div>
  );
}

/**
 * In-battle Bark card (Challenge game #2) — the three phases of my battle:
 *   • 'waiting'   — my challenge is out (or I just accepted); soft "waiting for {opponent}…" chip.
 *   • 'countdown' — the shared 3-2-1 (big numeral, self-ticked locally; purely presentational — the
 *     SERVER owns the real window) with a last-chance mic fix.
 *   • 'battle'    — "BARK!!!" + the live tug-of-war scoreboard (isolated store, see above).
 * Dual-coded, reduced-motion safe, and never aria-live at 10 Hz (the result card announces the end).
 */
function BarkBattleCard({
  match,
  micIssue,
  pttEnabled,
  onFixMic,
}: {
  match: BarkMatchView;
  micIssue: BarkMicIssue;
  pttEnabled: boolean;
  onFixMic: () => void;
}): JSX.Element {
  const reduceMotion = useReducedMotion();
  // Local 3-2-1 display for the countdown phase. Presentation-only: OfficePage flips the phase to
  // 'battle' on its own timer and the SERVER owns the authoritative window, so a drifted second here
  // can never change scoring. Re-armed per match.
  const [countLeft, setCountLeft] = useState(() => Math.ceil(match.countdownMs / 1000));
  useEffect(() => {
    if (match.phase !== 'countdown') return undefined;
    setCountLeft(Math.ceil(match.countdownMs / 1000));
    const timer = setInterval(() => setCountLeft((n) => Math.max(1, n - 1)), 1000);
    return () => clearInterval(timer);
  }, [match.phase, match.matchId, match.countdownMs]);

  if (match.phase === 'waiting') {
    return (
      <div
        role="status"
        aria-live="polite"
        aria-label={`Bark Battle: waiting for ${match.opponentName}.`}
        className="pointer-events-auto flex items-center gap-2 rounded-2xl border border-amber-300/40 bg-amber-400/15 px-4 py-2.5 text-sm font-medium text-amber-100 backdrop-blur-md shadow-lg"
      >
        <span
          aria-hidden="true"
          className={`h-2 w-2 rounded-full bg-amber-300 ${reduceMotion ? '' : 'animate-ping'}`}
        />
        <span aria-hidden="true" className="text-lg">🐶</span>
        <span>
          Waiting for <span className="font-semibold">{match.opponentName}</span>…
        </span>
      </div>
    );
  }

  if (match.phase === 'countdown') {
    return (
      <div
        role="status"
        aria-live="polite"
        aria-label={`Bark Battle against ${match.opponentName} starting — get ready to bark!`}
        className="pointer-events-auto flex w-[min(20rem,calc(100vw-1.5rem))] flex-col items-center gap-1.5 rounded-2xl border border-amber-300/40 bg-black/70 p-4 backdrop-blur-md shadow-2xl"
      >
        <div className="text-xs font-medium text-amber-100">
          <span aria-hidden="true">🐶</span> Bark Battle vs{' '}
          <span className="font-semibold">{match.opponentName}</span>
        </div>
        <div
          aria-hidden="true"
          className={`text-5xl font-black tabular-nums text-amber-300 ${reduceMotion ? '' : 'animate-pulse'}`}
        >
          {countLeft}
        </div>
        <div className="text-xs font-semibold uppercase tracking-wide text-white/80">
          Get ready to BARK!
        </div>
        {micIssue && (
          <div className="mt-1 w-full">
            <BarkMicWarning issue={micIssue} pttEnabled={pttEnabled} onFix={onFixMic} />
          </div>
        )}
      </div>
    );
  }

  // 'battle' — the window is OPEN.
  return (
    <div
      role="group"
      aria-label={`Bark Battle against ${match.opponentName} — bark now!`}
      className="pointer-events-auto flex w-[min(22rem,calc(100vw-1.5rem))] flex-col gap-2 rounded-2xl border border-amber-300/40 bg-black/70 p-4 backdrop-blur-md shadow-2xl"
    >
      <div
        className={`text-center text-2xl font-black tracking-wide text-amber-300 ${reduceMotion ? '' : 'animate-pulse'}`}
      >
        <span aria-hidden="true">🐶 </span>BARK!!!
      </div>
      <BarkScoreboard opponentName={match.opponentName} />
      {micIssue && <BarkMicWarning issue={micIssue} pttEnabled={pttEnabled} onFix={onFixMic} />}
    </div>
  );
}

/** Warm, dual-coded Bark Battle result copy from MY point of view. Scores present only when played. */
function barkResultText(r: BarkResultView): { headline: string; detail: string; icon: string } {
  const scores =
    r.yourScore !== undefined && r.theirScore !== undefined
      ? `${r.yourScore} – ${r.theirScore}`
      : '';
  switch (r.outcome) {
    case 'win':
      return {
        icon: '🏆',
        headline: `You out-barked ${r.opponentName}!`,
        detail: `${scores} — what a set of lungs! 🐶`,
      };
    case 'lose':
      return {
        icon: '🐶',
        headline: `${r.opponentName} out-barked you!`,
        detail: `${scores} — gg, rematch soon?`,
      };
    case 'draw':
      return { icon: '🤝', headline: 'A perfect bark-off — dead even!', detail: scores };
    case 'declined':
      return { icon: '🦆', headline: 'Maybe next time', detail: `${r.opponentName} passed for now.` };
    case 'expired':
      return { icon: '⌛', headline: 'The bark-off fizzled out', detail: 'No one answered in time.' };
    default:
      return { icon: '🦆', headline: 'Battle ended', detail: '' };
  }
}

/**
 * Warm, transient Bark Battle result card — the same choreography as {@link RpsResultCard} (slide-in
 * unless reduced motion, role="status" announcement, focus to Dismiss on show + restore to the
 * Challenge picker on unmount, parent auto-clear).
 */
function BarkResultCard({
  result,
  onDismiss,
  restoreFocusRef,
}: {
  result: BarkResultView;
  onDismiss: () => void;
  restoreFocusRef?: RefObject<HTMLButtonElement | null>;
}): JSX.Element {
  const reduceMotion = useReducedMotion();
  const { icon, headline, detail } = barkResultText(result);
  const enter = reduceMotion ? {} : { initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0 } };
  const dismissRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    dismissRef.current?.focus();
    const restore = restoreFocusRef?.current ?? null;
    return () => {
      if (restore && restore.isConnected && typeof restore.focus === 'function') restore.focus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <motion.div
      {...enter}
      role="status"
      aria-live="polite"
      aria-label={detail ? `${headline} ${detail}` : headline}
      className="pointer-events-auto flex items-center gap-3 rounded-2xl border border-amber-300/40 bg-amber-400/15 px-4 py-2.5 text-amber-100 backdrop-blur-md shadow-lg"
    >
      <span aria-hidden="true" className="text-2xl">{icon}</span>
      <div className="leading-tight">
        <div className="text-sm font-bold">{headline}</div>
        {detail && <div className="text-xs text-amber-100/80">{detail}</div>}
      </div>
      <button
        ref={dismissRef}
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss result"
        title="Dismiss"
        className="ml-1 rounded-lg border border-white/10 bg-white/5 px-2 py-1 text-xs font-medium text-white/80 transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
      >
        ✕
      </button>
    </motion.div>
  );
}

export function HUD({
  user,
  playerCount,
  netStatus,
  progression,
  guild,
  notice,
  voiceStatus,
  voiceMuted,
  cameraStatus,
  pttEnabled,
  micDevices,
  currentDeviceId,
  speakerDevices,
  currentSinkId,
  roomSections,
  audibleSessionIds,
  blockedPeers,
  voiceAnnouncement,
  myPresence,
  nearestObject,
  nearestSeat,
  nearestEntryAction,
  nearestDoorAction,
  isHost,
  doorState,
  doorways,
  mySeatedAt,
  atWorkstation,
  myHeldItem,
  meeting,
  worldEvent,
  guildPanel,
  chatLines,
  rpsInvite,
  rpsMatch,
  rpsResult,
  barkInvite,
  barkMatch,
  barkResult,
  onEmote,
  onToggleGuildPanel,
  onCloseGuildPanel,
  onNotice,
  onEnableVoice,
  onToggleMute,
  onToggleCamera,
  onTogglePtt,
  onSelectMic,
  onSelectSpeaker,
  onBlockCitizen,
  onUnblockCitizen,
  onSetPresence,
  onInteractNearest,
  onEntryAction,
  onDoorActionNearest,
  onHostToggleDoor,
  onSitNearest,
  onWork,
  onHoldItem,
  onLookChange,
  onSay,
  onReact,
  onSoundReact,
  onSoundPrefetch,
  onChallengeNearest,
  onRpsChoose,
  onRpsDecline,
  onDismissRpsResult,
  onBarkAccept,
  onBarkDecline,
  onDismissBarkResult,
  onCapturePhoto,
  onArmFurniture,
  onLogout,
}: HudProps): JSX.Element {
  if (import.meta.env.DEV) bumpRender('hud'); // perf-audit M4: count HUD reconciles (R1/R2 measurement)
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  // The invite-link toast used to linger forever with no way to close it. It now AUTO-DISMISSES after a
  // calm beat (long enough to read + copy) — but we PAUSE the countdown while the player is hovering or
  // has keyboard focus inside it, so it never vanishes mid-read/mid-copy (and a manual ✕ closes it early).
  const [invitePaused, setInvitePaused] = useState(false);
  // Re-copy feedback: a brief "Copied!" confirmation when the player taps the toast's Copy button (the
  // link is also auto-copied on create, but clipboard writes can be blocked, so an explicit Copy helps).
  const [inviteCopied, setInviteCopied] = useState(false);
  // M7a "Socialize" photo mode: hide all HUD chrome for a clean, framed shot of the world. A small,
  // calm overlay (capture + exit) is the ONLY chrome shown while active; Esc also exits. The accessible
  // toggle name matches /photo/i; nothing here is persisted beyond the (user-initiated) PNG download.
  const [photoMode, setPhotoMode] = useState(false);
  // FIX 1 — photo mode focus management (WCAG 2.4.3 focus order), mirroring PeoplePanel's
  // restore-to-opener pattern. Entering/exiting photo mode swaps to a different render tree (the
  // Capture/Exit bar vs. the full HUD), so without help focus falls to <body> both ways:
  //   • meToggleRef — the always-mounted "Me menu" TOGGLE button (NOT the in-popover "Photo mode" item,
  //     which is unmounted while photo mode is on + the menu is closed). This is the STABLE element we
  //     restore focus to on exit (Exit button OR Esc), so focus never falls to <body>.
  //   • photoCaptureRef — the Capture button in the photo bar; where we land focus on ENTER so the
  //     keyboard/SR user is inside the new chrome (not on <body>).
  // Deliberately NOT a focus trap — Esc/Exit always work; this is only on-enter focus + on-exit restore.
  const meToggleRef = useRef<HTMLButtonElement>(null);
  const photoCaptureRef = useRef<HTMLButtonElement>(null);
  // FIX 4 — the RPS result card's focus restore target. When the card appears it replaces the
  // match/invite surface (whose focused choice button has just unmounted → focus on <body>), and when it
  // later unmounts (auto-clear or Dismiss) focus would fall to <body> again. The Challenge button is a
  // STABLE always-mounted landing spot, so the result card pulls focus to itself on show and hands it
  // back here on dismiss — mirroring the invite-dialog/PeoplePanel restore-to-opener pattern (WCAG 2.4.3).
  const challengeButtonRef = useRef<HTMLButtonElement>(null);
  // Skip the very first render (photoMode starts false): only move focus on an actual enter/exit, never
  // steal it when the HUD first mounts.
  const photoModePrev = useRef(photoMode);
  useEffect(() => {
    if (photoModePrev.current === photoMode) return;
    photoModePrev.current = photoMode;
    if (photoMode) {
      // Entered: pull focus into the photo control bar (the Capture button).
      const target = photoCaptureRef.current;
      if (target && target.isConnected) target.focus();
    } else {
      // Exited (Exit button or Esc): the in-popover "Photo mode" item we opened from is unmounted, so
      // restore focus to the STABLE, always-mounted "Me menu" toggle button instead of falling to <body>.
      const opener = meToggleRef.current;
      if (opener && opener.isConnected) opener.focus();
    }
  }, [photoMode]);
  // Exit photo mode on Esc (in addition to the on-screen Exit button) — a clear, expected way out.
  useEffect(() => {
    if (!photoMode) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setPhotoMode(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [photoMode]);
  // Declutter pass: ONE top-level menu open at a time (Guild / Me / World / People), or null for the
  // calm default. Opening one closes the rest, so the screen never fills with stacked panels. Each
  // control inside keeps its SAME accessible name — it's just tucked under a discoverable menu now.
  const [menu, setMenu] = useState<HudMenu>(null);
  // Toggle a menu (clicking the open one closes it); opening any menu closes the others (one at a time).
  const toggleMenu = (which: Exclude<HudMenu, null>): void =>
    setMenu((prev) => (prev === which ? null : which));
  const closeMenu = (): void => setMenu(null);
  // The action-bar interaction menus (Reactions · Sounds · Animations · Hold): one shared controller owns
  // which is open + the type-a-number selection. The number keys 1–4 open them; while open, digit keys
  // pick an item by number (see useInteractionMenus). This is independent of the top-level `menu` above.
  const menus = useInteractionMenus(MENU_HOTKEYS);
  // M6 "Express Yourself" Store + Wardrobe: a slide-over opened from the Me menu. Kept as its own piece
  // of state (not a `menu` value) because — like the guild slide-over — it's a richer surface that opens
  // BESIDE the menu bar and stays put while you browse; opening it tucks the Me menu away. The toggle's
  // accessible name still matches /shop|store|wardrobe|cosmetics/i.
  const [wardrobeOpen, setWardrobeOpen] = useState(false);
  // The Account slide-over (UI redesign): identity + progress + all personal settings (audio, mic,
  // camera, presence, owner world-theme) gathered in one place, opened from the main (burger) menu.
  const [accountOpen, setAccountOpen] = useState(false);
  // Google-Meet-style device chooser: the mic + speaker selectors stay tucked away until the player
  // opens them with the chevron on the mic button (collapsed by default so the HUD stays calm).
  const [voiceSettingsOpen, setVoiceSettingsOpen] = useState(false);
  const canInvite = roleAtLeast(user.role, 'ADMIN');
  // M8 "Guilds at Scale": only the guild OWNER may re-skin the shared world (the theme is guild-wide,
  // Respect the OS "reduce motion" setting (a11y): flatten the HUD's slide/fade entrances to no-ops so
  // there's no movement for motion-sensitive players. The information is identical either way.
  const reduceMotion = useReducedMotion();
  // A small slide-down entrance (invite link) flattened under reduced motion.
  const slideDownSm = reduceMotion
    ? {}
    : { initial: { opacity: 0, y: -8 }, animate: { opacity: 1, y: 0 } };
  // The transient notice (enter + exit) — flattened to no movement under reduced motion.
  const noticeMotion = reduceMotion
    ? {}
    : { initial: { opacity: 0, y: 12 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: 12 } };

  const invite = useMutation({
    mutationFn: () => api.createInvite({ role: 'EMPLOYEE', maxUses: null, expiresAt: null }),
    onSuccess: (res) => {
      setInviteUrl(res.inviteUrl);
      setInviteCopied(false);
      void navigator.clipboard?.writeText(res.inviteUrl).catch(() => undefined);
    },
  });

  // Auto-dismiss the invite toast after a calm beat so it never gets stuck on screen. The timer RESETS
  // whenever a fresh link is minted (inviteUrl changes) and PAUSES while the toast is hovered/focused
  // (invitePaused) so it can't disappear while it's being read or copied. Cleaned up on every change.
  useEffect(() => {
    if (!inviteUrl || invitePaused) return undefined;
    const t = window.setTimeout(() => setInviteUrl(null), 9000);
    return () => window.clearTimeout(t);
  }, [inviteUrl, invitePaused]);

  // Photo mode (M7a): a clean, framed view of the world with ALL HUD chrome hidden. The only thing on
  // screen is a small, calm control bar to capture a PNG or exit (Esc also exits). Keyboard-reachable
  // with explicit accessible names; nothing is persisted beyond the user-initiated download.
  if (photoMode) {
    return (
      <div className="pointer-events-none absolute inset-0 select-none">
        <div className="pointer-events-auto absolute bottom-6 left-1/2 flex -translate-x-1/2 items-center gap-2 rounded-2xl border border-white/10 bg-black/50 p-2 backdrop-blur-md">
          <span className="px-2 text-xs font-medium text-white/60">📸 Photo mode</span>
          <button
            ref={photoCaptureRef}
            type="button"
            onClick={() => onCapturePhoto()}
            aria-label="Capture photo"
            title="Download a PNG of the office"
            className="flex items-center gap-1 rounded-xl border border-amber-300/40 bg-amber-400/15 px-3 py-2 text-sm font-medium text-amber-100 transition hover:bg-amber-400/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/50"
          >
            <span aria-hidden="true">📷</span> Capture
          </button>
          <button
            type="button"
            onClick={() => setPhotoMode(false)}
            aria-label="Exit photo mode"
            title="Exit photo mode (Esc)"
            className="rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm font-medium text-white/80 transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
          >
            Exit
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pointer-events-none absolute inset-0 select-none">
      {/* Visually-hidden polite live region (M3.4, FIX 2 / WCAG 4.1.3): blocking a voice is a silent,
          private action with no visible toast, so announce it to screen readers here. `sr-only` keeps it
          off-screen but in the a11y tree; aria-live="polite" reads it after the current utterance. */}
      <div role="status" aria-live="polite" className="sr-only">
        {voiceAnnouncement}
      </div>

      {/* The top-centre SENSE-OF-PLACE column was relocated into the World menu (top-right) by request —
          which world · which room · time/weather · who's here, plus the connection / world-event / meeting
          indicators, now all live inside that one entry point (see the World menu below). Nothing floats
          at top-centre anymore.

          One always-mounted, VISUALLY-HIDDEN occupancy line stays here on purpose: the visible "{N} in
          the office" chip is now behind the (collapsed) World menu, so this `sr-only` mirror keeps the
          glanceable count available to screen-reader users at all times (an a11y win — it's no longer
          trapped in a closed menu) and preserves the established e2e read target `/\d+ in the office/`.
          It is invisible, so the visual request — nothing at top-centre — is fully honoured. */}
      <p className="sr-only" aria-label={`${playerCount} ${playerCount === 1 ? 'duck' : 'ducks'} in the office`}>
        {playerCount} in the office
      </p>

      {/* Live status, FLOATING at top-centre (UI redesign): the connection-health pill, the shared
          world-event banner, and the meeting progress bar. These are transient, time-sensitive cues, so
          they live in the open (not buried in a menu) — each renders nothing when idle, so the strip is
          invisible by default and the world stays the focus. The container isn't pointer-events-none so
          the "Connection lost" reload button stays clickable; empty children collapse it to zero size. */}
      <div className="absolute left-1/2 top-2 z-30 flex max-w-[calc(100vw-1rem)] -translate-x-1/2 flex-col items-center gap-2 sm:top-3">
        <ConnectionIndicator status={netStatus} />
        <WorldEventBanner worldEvent={worldEvent} />
        <MeetingBar meeting={meeting} />
      </div>

      {/* The main-menu surfaces (Account / Quests & Achievements / Leaderboard / Store) all open under the
          burger button on the right — they're rendered INSIDE the top-right menu cluster below, so they
          flow under the icons exactly like the Store panel and share its width/padding. */}

      {/* Top-right: the calm menu bar. Constant essentials stay visible (mic — never hidden, ADR-0009;
          camera) plus the single ☰ main menu + the People-nearby roster. The richer surfaces (Account /
          Quests & Achievements / Leaderboard / Store) open under the burger, right-aligned, one at a time. */}
      <div className="absolute right-2 top-2 z-20 flex max-w-[calc(100vw-1rem)] flex-col items-end gap-2 sm:right-3 sm:top-3 lg:right-4 lg:top-4">
        <div className="pointer-events-auto flex flex-wrap items-center justify-end gap-2">
          {/* Webcam "show your face" (ADR-0030): the twin of the mic, just to its left — Camera = let them
              see me. Always visible so LIVE is glanceable + the kill switch is one click. Opt-in; off is
              the unremarkable norm (the button quietly hides if the device has no camera). */}
          <CamButton status={cameraStatus} onToggleCamera={onToggleCamera} />
          {/* Proximity voice (ADR-0009): a constant essential — visible state always (enable / live /
              muted / denied). Never tucked into a menu. */}
          <MicButton
            status={voiceStatus}
            muted={voiceMuted}
            pttEnabled={pttEnabled}
            onEnableVoice={onEnableVoice}
            onToggleMute={onToggleMute}
            devicesOpen={voiceSettingsOpen}
            onToggleDevices={() => setVoiceSettingsOpen((v) => !v)}
          />
          {/* The MAIN (burger) menu — the single point of entry that replaced the old Guild / Me / World
              menus (UI redesign). Icon-only ☰, no text label, so the top-right corner reads as a calm
              row of icons over the world (Cam · Mic · People · ☰), not an admin panel. Each item opens a
              focused surface; one menu open at a time. `meToggleRef` is the stable focus-restore target
              for photo mode (it now launches from inside Account, which unmounts while photo mode is on). */}
          <HudMenu
            open={menu === 'main'}
            label="Menu"
            icon="☰"
            toggleRef={meToggleRef}
            onToggle={() => toggleMenu('main')}
            onClose={closeMenu}
          >
            <MenuItem
              icon="🦆"
              label="Account"
              active={accountOpen}
              title="Your profile, progress & personal settings"
              onClick={() => {
                onCloseGuildPanel();
                setWardrobeOpen(false);
                setAccountOpen(true);
                closeMenu();
              }}
            />
            <MenuItem
              icon="🗺️"
              label="Quests & Achievements"
              active={guildPanel === 'quests' || guildPanel === 'achievements'}
              title="Cooperative quests and the badges you’ve earned"
              onClick={() => {
                setAccountOpen(false);
                setWardrobeOpen(false);
                onToggleGuildPanel('quests');
                closeMenu();
              }}
            />
            {/* Store (M6): opens the cosmetic shop/closet. Accessible name kept matching
                /shop|store|wardrobe|cosmetics/i so it stays the stable target. */}
            <MenuItem
              icon="🛍️"
              label="Store"
              ariaLabel="Store & Wardrobe"
              active={wardrobeOpen}
              title="Spend earned coins on cosmetics — looks only, never an advantage."
              onClick={() => {
                setAccountOpen(false);
                onCloseGuildPanel();
                setWardrobeOpen(true);
                closeMenu();
              }}
            />
            <MenuItem
              icon="🏆"
              label="Leaderboard"
              active={guildPanel === 'leaderboards'}
              title="Positive, opt-in boards celebrating trust and helpfulness"
              onClick={() => {
                setAccountOpen(false);
                setWardrobeOpen(false);
                onToggleGuildPanel('leaderboards');
                closeMenu();
              }}
            />
            {/* Invite — one of the most important social mechanics, so it's visually lifted (highlight
                tone). Kept accessible name "Invite teammate" as the stable target. */}
            {canInvite && (
              <MenuItem
                icon="＋"
                label={invite.isPending ? 'Creating invite…' : 'Invite friend'}
                ariaLabel="Invite teammate"
                tone="highlight"
                title="Create a shareable invite link for a friend"
                onClick={() => {
                  invite.mutate();
                  closeMenu();
                }}
              />
            )}
            {/* Log out — visually separated + danger-toned, the last action. */}
            <div className="my-1 border-t border-white/10" aria-hidden="true" />
            <MenuItem
              icon="🚪"
              label="Log out"
              tone="danger"
              title="Sign out of the office"
              onClick={() => {
                closeMenu();
                onLogout();
              }}
            />
          </HudMenu>

          {/* TODO(host-panel): a future OWNER-only "Host / room management" surface goes here — a
              host-gated icon (e.g. 🛡) opening a Store-style right slide-over that re-homes the world-theme
              picker + ship-a-release (both removed from the UI in the redesign; server features intact —
              ShipReleaseControl is still exported from GuildPanel; WorldThemePicker restorable from git
              6a35625^) plus the host Doors control. Then un-retire e2e/m8-themes.mjs + restore m5's UI
              ship. See memory: duck-office-room-ui-redesign. */}

          {/* People nearby / voice-block (M3.4): one of the top-level entry points. Its body is the
              existing focus-managed PeoplePanel (rendered below the bar), so it routes through the same
              single-menu state (one open at a time). Always available so a citizen can manage existing
              blocks even before re-enabling voice. Accessible name matches /people|nearby|voice/i. */}
          <HudMenu
            open={menu === 'people'}
            label="People nearby"
            icon="👥"
            badge={blockedPeers.length}
            onToggle={() => toggleMenu('people')}
            onClose={closeMenu}
          >
            <PeoplePanel
              roomSections={roomSections}
              audibleSessionIds={audibleSessionIds}
              blockedPeers={blockedPeers}
              onBlockCitizen={onBlockCitizen}
              onUnblockCitizen={onUnblockCitizen}
              embedded
            />
          </HudMenu>
        </div>
        {/* Voice accessibility + push-to-talk (M3.3): only once the mic is live. A constant essential
            while you're talking — it stays beside the mic button, never hidden in a menu. */}
        {voiceStatus === 'live' && (
          <VoicePanel
            pttEnabled={pttEnabled}
            micDevices={micDevices}
            currentDeviceId={currentDeviceId}
            onTogglePtt={onTogglePtt}
            onSelectMic={onSelectMic}
            speakerDevices={speakerDevices}
            currentSinkId={currentSinkId}
            onSelectSpeaker={onSelectSpeaker}
            devicesOpen={voiceSettingsOpen}
          />
        )}
        {/* Store & Wardrobe (M6 "Express Yourself", emotion: Identity): the cosmetic shop + closet.
            Opt-in, kind, non-pressuring — making your duck yours. Equipping re-broadcasts the look. */}
        <AnimatePresence>
          {wardrobeOpen && (
            <Wardrobe
              key="wardrobe"
              onClose={() => setWardrobeOpen(false)}
              onLookChange={onLookChange}
              onNotice={onNotice}
              onArmFurniture={(item) => {
                // Buy & Place: hand the chosen item up to the engine's placement ghost, then close the
                // Store so the player can see + click their room. The buy happens on confirmed placement.
                setWardrobeOpen(false);
                onArmFurniture(item);
              }}
            />
          )}
        </AnimatePresence>

        {/* Quests & Achievements / Leaderboard slide-overs — open here, under the burger, right-aligned
            and with the SAME width/padding as Store (they share the GuildPanel `Panel`). */}
        <GuildPanels tab={guildPanel} onClose={onCloseGuildPanel} />

        {/* Account slide-over — the single home for WHO you are + personal settings. Opens under the
            burger like Store; same width/padding. (Where-you-are, world-theme and ship-a-release were
            removed by request — not useful right now.) */}
        <AnimatePresence>
          {accountOpen && (
            <Panel title="Account" icon="🦆" onClose={() => setAccountOpen(false)}>
              {/* Identity */}
              <div className="mb-3 flex items-center gap-3">
                <span
                  aria-hidden="true"
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/15 bg-white/5 text-2xl"
                >
                  🦆
                </span>
                <div className="min-w-0">
                  <div className="truncate text-sm font-bold text-white">{user.displayName}</div>
                  {progression && (
                    <div className="text-[11px] text-white/55">Level {progression.xpLevel}</div>
                  )}
                </div>
              </div>

              {/* The three INDEPENDENT progressions (never one score) + coins. */}
              <MenuSectionLabel>Your progress</MenuSectionLabel>
              <div className="flex flex-wrap gap-2">
                <StatChip
                  icon="⭐"
                  label="Reputation"
                  value={progression ? String(progression.reputation) : '—'}
                />
                <StatChip
                  icon="🎯"
                  label="XP"
                  value={progression ? `Lv ${progression.xpLevel}` : '—'}
                />
                <StatChip icon="🏰" label="Guild" value={guild ? `Lv ${guild.level}` : '—'} />
                <CoinChip coins={progression?.coins} />
              </div>
              {progression && (
                <div className="mt-1 px-1 text-[11px] text-white/50">
                  {progression.reputationBudgetRemaining} reputation left to give today
                </div>
              )}

              <MenuSectionLabel>Presence flair</MenuSectionLabel>
              <PresencePicker myPresence={myPresence} onSetPresence={onSetPresence} embedded />

              <MenuSectionLabel>Audio</MenuSectionLabel>
              <AudioSettingsPanel />

              <MenuSectionLabel>Performance</MenuSectionLabel>
              <RenderQualityControl />

              <MenuSectionLabel>More</MenuSectionLabel>
              <MenuItem
                icon="📸"
                label="Photo mode"
                title="Hide the HUD for a clean shot of the office — then capture a PNG."
                onClick={() => {
                  setAccountOpen(false);
                  setPhotoMode(true);
                }}
              />
            </Panel>
          )}
        </AnimatePresence>
      </div>

      {/* Invite-link toast (admin). Auto-dismisses after a beat (so it never gets stuck), pauses on
          hover/focus so it can be read + copied, and offers an explicit Copy + a ✕ close. role="status"
          announces "Invite link copied!" to screen readers. The `.font-mono` URL is the e2e read target. */}
      <AnimatePresence>
        {inviteUrl && (
          <motion.div
            {...slideDownSm}
            exit={reduceMotion ? undefined : { opacity: 0, y: -8 }}
            role="status"
            aria-live="polite"
            onMouseEnter={() => setInvitePaused(true)}
            onMouseLeave={() => setInvitePaused(false)}
            onFocus={() => setInvitePaused(true)}
            onBlur={() => setInvitePaused(false)}
            className="pointer-events-auto absolute right-2 top-16 z-30 w-[min(24rem,calc(100vw-1rem))] rounded-xl border border-amber-300/30 bg-slate-950/85 px-3 py-2.5 text-xs text-amber-100 shadow-xl backdrop-blur-md sm:right-3 lg:right-4"
          >
            <div className="flex items-start justify-between gap-2">
              <span className="font-semibold">✨ Invite link copied! Share it:</span>
              <button
                type="button"
                onClick={() => setInviteUrl(null)}
                aria-label="Dismiss invite link"
                title="Dismiss"
                className="-mr-1 -mt-0.5 shrink-0 rounded-md px-1.5 text-sm leading-none text-amber-200/70 transition hover:bg-white/10 hover:text-amber-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/50"
              >
                ✕
              </button>
            </div>
            <div className="mt-1 break-all font-mono text-amber-200">{inviteUrl}</div>
            <div className="mt-2 flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  void navigator.clipboard
                    ?.writeText(inviteUrl)
                    .then(() => {
                      setInviteCopied(true);
                      window.setTimeout(() => setInviteCopied(false), 1500);
                    })
                    .catch(() => undefined);
                }}
                aria-label="Copy invite link"
                className="rounded-lg border border-amber-300/40 bg-amber-400/15 px-2.5 py-1 text-[11px] font-semibold text-amber-100 transition hover:bg-amber-400/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/50"
              >
                {inviteCopied ? '✓ Copied' : '📋 Copy'}
              </button>
              <span className="text-[10px] text-amber-100/50">Auto-hides in a few seconds</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Interact + Sit + Work prompts (M4 + Task 2 + gamified-XP): shown when a world object / seat is in
          range, while seated, or while seated at a desk. Dual-coded (icon + label) buttons, so they're
          operable without the in-world E keypress (a11y gate). Stacked centrally just above the emote bar.
          The WORK prompt appears only when seated at a workstation desk — a repeatable +1 XP beat. */}
      {(nearestObject ||
        nearestSeat ||
        mySeatedAt ||
        atWorkstation ||
        nearestEntryAction ||
        nearestDoorAction) && (
        <div className="absolute bottom-20 left-3/4 flex max-w-[calc(50vw-1.5rem)] -translate-x-1/2 flex-col items-center gap-2 lg:left-1/2 lg:max-w-none">
          {nearestEntryAction && (
            <EntryActionPrompt action={nearestEntryAction} onAction={onEntryAction} />
          )}
          {nearestDoorAction && (
            <DoorActionPrompt action={nearestDoorAction} onAction={onDoorActionNearest} />
          )}
          {nearestObject && <InteractPrompt object={nearestObject} onInteract={onInteractNearest} />}
          <SeatPrompt nearestSeat={nearestSeat} seated={mySeatedAt !== ''} onSit={onSitNearest} />
          {atWorkstation && <WorkPrompt onWork={onWork} />}
        </div>
      )}

      {/* The unified bottom-centre ACTION BAR (UX declutter pass): one calm block with the four
          "express yourself" disclosures — Reactions · Sounds · Animations · Hold — plus the Challenge tap.
          The chat input lives in its own resizable window now (bottom-left), so this row is purely the
          quick expressive actions. */}
      <div className="absolute bottom-2 left-1/2 z-20 flex max-w-[calc(100vw-1rem)] -translate-x-1/2 flex-col items-center gap-1.5 sm:bottom-3 lg:bottom-4">
        {/* Type-a-number echo: while a menu is open and digits are being typed, show the running number so
            the player can SEE what they're about to pick (and that a two-digit number is still building).
            Decorative + aria-hidden — the selection itself is what's announced when it lands. */}
        {menus.buffer && (
          <div
            aria-hidden="true"
            className="pointer-events-none rounded-lg border border-amber-300/40 bg-black/70 px-2.5 py-1 text-sm font-bold tabular-nums text-amber-100 shadow-lg backdrop-blur-md"
          >
            #{menus.buffer}
          </div>
        )}
        <ActionBar
          menus={menus}
          onReact={onReact}
          onSoundReact={onSoundReact}
          onSoundPrefetch={onSoundPrefetch}
          onEmote={onEmote}
          myHeldItem={myHeldItem}
          onHoldItem={onHoldItem}
          onChallengeNearest={onChallengeNearest}
          challengeDisabled={
            rpsMatch !== null || rpsInvite !== null || barkMatch !== null || barkInvite !== null
          }
          challengeRef={challengeButtonRef}
          isHost={isHost}
          doorState={doorState}
          doorways={doorways}
          onHostToggleDoor={onHostToggleDoor}
        />
      </div>

      {/* M7b-2 "Play Together": the RPS surfaces. The invite dialog (focus-managed) and the in-match
          card share the centre-bottom area above the emote bar; the result card floats centre. At most
          one is meaningful at a time (invite → match → result). */}
      <div className="absolute bottom-28 left-3/4 flex max-w-[calc(50vw-1.5rem)] -translate-x-1/2 flex-col items-center gap-2 lg:left-1/2 lg:max-w-none">
        {rpsInvite && (
          <RpsInviteDialog
            invite={rpsInvite}
            onChoose={(choice) => onRpsChoose(rpsInvite.matchId, choice)}
            onDecline={() => onRpsDecline(rpsInvite.matchId)}
          />
        )}
        {!rpsInvite && rpsMatch && (
          <RpsMatchCard
            match={rpsMatch}
            onChoose={(choice) => onRpsChoose(rpsMatch.matchId, choice)}
          />
        )}
        <AnimatePresence>
          {rpsResult && (
            <RpsResultCard
              key={rpsResult.matchId}
              result={rpsResult}
              onDismiss={onDismissRpsResult}
              restoreFocusRef={challengeButtonRef}
            />
          )}
        </AnimatePresence>
        {/* Bark Battle surfaces (Challenge game #2) share the same centre-bottom stack; the server's
            cross-minigame busy check means at most ONE game's surfaces are ever live at a time. The mic
            issue is recomputed per render so the warning disappears the instant voice/mute is fixed. */}
        {barkInvite && (
          <BarkInviteDialog
            invite={barkInvite}
            micIssue={voiceStatus !== 'live' ? 'off' : voiceMuted ? 'muted' : null}
            pttEnabled={pttEnabled}
            onFixMic={voiceStatus !== 'live' ? onEnableVoice : onToggleMute}
            onAccept={() => onBarkAccept(barkInvite.matchId)}
            onDecline={() => onBarkDecline(barkInvite.matchId)}
          />
        )}
        {!barkInvite && barkMatch && (
          <BarkBattleCard
            match={barkMatch}
            micIssue={voiceStatus !== 'live' ? 'off' : voiceMuted ? 'muted' : null}
            pttEnabled={pttEnabled}
            onFixMic={voiceStatus !== 'live' ? onEnableVoice : onToggleMute}
          />
        )}
        <AnimatePresence>
          {barkResult && (
            <BarkResultCard
              key={barkResult.matchId}
              result={barkResult}
              onDismiss={onDismissBarkResult}
              restoreFocusRef={challengeButtonRef}
            />
          )}
        </AnimatePresence>
      </div>

      {/* The MMO-style chat window (M7a) — bottom-left, resizable. Pairs the accessible "recent chatter"
          log (a polite aria-live region, since the Pixi bubbles/emoji are invisible to AT) with the
          speech input moved here from the action bar. Chatter is EPHEMERAL — never saved (Art.1). */}
      {/* Chat: bottom-left. On phones the centred action bar sits at the very bottom, so lift the chat
          above it (bottom-20) to stop the two overlapping; desktop/tablet keep them side-by-side. */}
      <div className="absolute bottom-20 left-2 z-10 md:bottom-4 md:left-4">
        <ChatPanel lines={chatLines} onSay={onSay} onReplaySound={onSoundReact} />
      </div>

      {/* Controls hint — keyboard-only, so hidden below lg (tablets are touch devices with no physical
          keyboard, and on narrow widths it would collide with the centred action bar). */}
      <div className="absolute bottom-4 right-4 hidden rounded-xl border border-white/10 bg-black/30 px-3 py-2 text-right text-xs text-white/50 lg:block">
        <div>
          <kbd>W A S D</kbd> / arrows to move
        </div>
        <div>scroll to zoom · 1–4 open a menu, then type a number</div>
        <div className="text-amber-200/70">
          <kbd>G</kbd> give ⭐ reputation to a nearby duck
        </div>
        <div className="text-amber-200/70">
          <kbd>E</kbd> sit / use a nearby object
        </div>
        <div className="text-amber-200/70">🪨📄✂️ challenge a nearby duck to play</div>
        <div className="text-emerald-200/70">💻 sit at a desk, then Work for XP</div>
      </div>

      {/* Transient notice (reputation given, errors) */}
      <AnimatePresence>
        {notice && (
          <motion.div
            {...noticeMotion}
            className="pointer-events-none absolute bottom-24 left-1/2 -translate-x-1/2 rounded-xl border border-amber-300/30 bg-amber-400/15 px-4 py-2 text-sm font-medium text-amber-100"
          >
            {notice}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
